import { logger } from '@fe/core';
import { AlternateDestinationRecordType, FPMMessageType } from '../common/enums';
import assert from '../utils/assert';
import { MessageId } from './message-id';

export interface GEO_COORD_INT {
    wLongitude: number;
    wLatitude: number;
    cLonUpper4: number;
    cLatUpper4: number;
}

export function parseGEO_COORD_INT(buffer: ArrayBuffer): GEO_COORD_INT {
    const view = new DataView(buffer);
    const wLongitude = view.getUint16(0, true);
    const wLatitude = view.getUint16(2, true);

    // Decoding bits 4,4
    const tmp4 = view.getUint8(4);
    const cLonUpper4 = tmp4 & 0xf;
    const cLatUpper4 = (tmp4 & 0xf0) >>> 4;
    // Total size: 5 bytes
    return {
        wLongitude,
        wLatitude,
        cLonUpper4,
        cLatUpper4
    };
}

export interface WthrProdStatusItemV2 {
    wProductID: number;
    cFlags: number;
    dwFEWPTimeServer1: number;
    dwFEWPTimeServer2: number;
}

export function parseWthrProdStatusItemV2(buffer: ArrayBuffer): WthrProdStatusItemV2 {
    const view = new DataView(buffer);
    const wProductID = view.getUint16(0, true);
    const cFlags = view.getUint8(2);
    const dwFEWPTimeServer1 = view.getUint32(3, true);
    const dwFEWPTimeServer2 = view.getUint32(7, true);
    // Total size: 11 bytes
    return {
        wProductID,
        cFlags,
        dwFEWPTimeServer1,
        dwFEWPTimeServer2
    };
}

export interface SYSTEMTIME {
    wYear: number;
    wMonth: number;
    wDayOfWeek: number;
    wDay: number;
    wHour: number;
    wMinute: number;
    wSecond: number;
    wMilliseconds: number;
}

export function parseSYSTEMTIME(buffer: ArrayBuffer): SYSTEMTIME {
    const view = new DataView(buffer);
    const wYear = view.getUint16(0, true);
    const wMonth = view.getUint16(2, true);
    const wDayOfWeek = view.getUint16(4, true);
    const wDay = view.getUint16(6, true);
    const wHour = view.getUint16(8, true);
    const wMinute = view.getUint16(10, true);
    const wSecond = view.getUint16(12, true);
    const wMilliseconds = view.getUint16(14, true);
    // Total size: 16 bytes
    return {
        wYear,
        wMonth,
        wDayOfWeek,
        wDay,
        wHour,
        wMinute,
        wSecond,
        wMilliseconds
    };
}

export interface FE_DATETIME {
    iJDay: number;
    wHour: number;
    wMin: number;
}

export function parseFE_DATETIME(buffer: ArrayBuffer): FE_DATETIME {
    const view = new DataView(buffer);
    const iJDay = view.getInt32(0, true);

    // Decoding bits 5,6,5
    const tmp4 = view.getUint16(4, true);
    const wHour = tmp4 & 0x1f;
    const wMin = (tmp4 & 0x7e0) >>> 5;
    // Total size: 6 bytes
    return {
        iJDay,
        wHour,
        wMin
    };
}

export function parseFE_SMALLDATETIME(buffer: ArrayBuffer, iJDay: number): FE_DATETIME {
    const view = new DataView(buffer);
    // Decoding bits 3,5,6,2
    const tmp4 = view.getUint16(0, true);
    const wJDayOffset = tmp4 & 0x7;
    const wHour = (tmp4 & 0xf8) >>> 3;
    const wMin = (tmp4 & 0x7e0) >>> 5;

    return {
        iJDay: iJDay + wJDayOffset,
        wHour,
        wMin
    };
}

export interface SMSG_TAF_REC {
    sStartTime: FE_DATETIME;
    sEndTime: FE_DATETIME;
    cSeq: number;
    cType: number;
    cProbability: number;
    wWindDir: number;
    cWindSpeed: number;
    cWindGust: number;
    cVisLessGrt: number;
    wVisibility: number;
    wWeatherBitmap: number;
    wCeiling: number;
    cFlightCat: number;
}

export function parseSMSG_TAF_REC(buffer: ArrayBuffer): SMSG_TAF_REC {
    const view = new DataView(buffer);
    const sStartTime = parseFE_DATETIME(buffer.slice(0));
    const sEndTime = parseFE_DATETIME(buffer.slice(6));
    const cSeq = view.getUint8(12);
    const cType = view.getUint8(13);
    const cProbability = view.getUint8(14);
    const wWindDir = view.getUint16(15, true);
    const cWindSpeed = view.getUint8(17);
    const cWindGust = view.getUint8(18);
    const cVisLessGrt = view.getUint8(19);
    const wVisibility = view.getUint16(20, true);
    const wWeatherBitmap = view.getUint16(22, true);
    const wCeiling = view.getUint16(24, true);
    const cFlightCat = view.getUint8(26);
    // Total size: 27 bytes
    return {
        sStartTime,
        sEndTime,
        cSeq,
        cType,
        cProbability,
        wWindDir,
        cWindSpeed,
        cWindGust,
        cVisLessGrt,
        wVisibility,
        wWeatherBitmap,
        wCeiling,
        cFlightCat
    };
}

export interface SMSG_RVR_REC {
    pcRunwayID: string;
    cTDVis: number;
    cMPVis: number;
    cROVis: number;
    cTDVisTrend: number;
    cMPVisTrend: number;
    cROVisTrend: number;
    cLightsEdge: number;
    cLightsCenter: number;
}

export function parseSMSG_RVR_REC(buffer: ArrayBuffer): SMSG_RVR_REC {
    const view = new DataView(buffer);
    const chars_0 = [];
    for (let i = 0; i < 3; i++) {
        const char = view.getUint8(0 + i);
        if (char === 0) break;
        chars_0.push(String.fromCharCode(char));
    }
    const pcRunwayID = chars_0.join('').trim();
    const cTDVis = view.getUint8(3);
    const cMPVis = view.getUint8(4);
    const cROVis = view.getUint8(5);
    const cTDVisTrend = view.getUint8(6);
    const cMPVisTrend = view.getUint8(7);
    const cROVisTrend = view.getUint8(8);
    const cLightsEdge = view.getUint8(9);
    const cLightsCenter = view.getUint8(10);
    // Total size: 11 bytes
    return {
        pcRunwayID,
        cTDVis,
        cMPVis,
        cROVis,
        cTDVisTrend,
        cMPVisTrend,
        cROVisTrend,
        cLightsEdge,
        cLightsCenter
    };
}

export interface SMSG_DELAY_REC {
    cType: number;
    sStartTime: FE_DATETIME;
    sStopTime: FE_DATETIME;
    wDelayMinutesMinAvg: number;
    wDelayMinutesMax: number;
    cTrend: number;
    bDeleted: boolean;
    pcReason: string;
}

export function parseSMSG_DELAY_REC(buffer: ArrayBuffer): SMSG_DELAY_REC {
    const view = new DataView(buffer);
    const cType = view.getUint8(0);
    const sStartTime = parseFE_DATETIME(buffer.slice(1));
    const sStopTime = parseFE_DATETIME(buffer.slice(7));
    const wDelayMinutesMinAvg = view.getUint16(13, true);
    const wDelayMinutesMax = view.getUint16(15, true);
    const cTrend = view.getUint8(17);
    const bDeleted = view.getUint8(18) !== 0;
    const chars_19 = [];
    for (let i = 0; i < 50; i++) {
        const char = view.getUint8(19 + i);
        if (char === 0) break;
        chars_19.push(String.fromCharCode(char));
    }
    const pcReason = chars_19.join('').trim();
    // Total size: 69 bytes
    return {
        cType,
        sStartTime,
        sStopTime,
        wDelayMinutesMinAvg,
        wDelayMinutesMax,
        cTrend,
        bDeleted,
        pcReason
    };
}

export interface EVENT_REC_IMPACT {
    wEventCode: number;
    pcObjectID: string;
    cState: number;
    bUsed: number;
    bCompliant: number;
    bDetails: number;
    cClass: number;
    cIndex: number;
    wFeedID: number;
    wProductID: number;
    cProdVendor: number;
    cProdType: number;
    dwProductValidTime: number;
    dwProductModelTime: number;
    cConfidence: number;
    cSeverity: number;
    wDistanceOff: number;
    wAltBase: number;
    wAltTop: number;
    dwRouteID: number;
    wStartPtIdx: number;
    wStartPtOff: number;
    wResetPtIdx: number;
    wResetPtOff: number;
    nStartLat100k: number;
    nStartLon100k: number;
    nEndLat100k: number;
    nEndLon100k: number;
    dwEnterTime: number;
    dwExitTime: number;
    dwCreateTime: number;
    dwRemoveTime: number;
    dwUpdTime: number;
    pcGuid: number[];
}

export function parseEVENT_REC_IMPACT(buffer: ArrayBuffer): EVENT_REC_IMPACT {
    const view = new DataView(buffer);
    const wEventCode = view.getUint16(0, true);
    const chars_2 = [];
    for (let i = 0; i < 6; i++) {
        const char = view.getUint8(2 + i);
        if (char === 0) break;
        chars_2.push(String.fromCharCode(char));
    }
    const pcObjectID = chars_2.join('').trim();

    // Decoding bits 2,1,1,1,3
    const tmp8 = view.getUint8(8);
    const cState = tmp8 & 0x3;
    const bUsed = (tmp8 & 0x4) >>> 2;
    const bCompliant = (tmp8 & 0x8) >>> 3;
    const bDetails = (tmp8 & 0x10) >>> 4;
    const cClass = (tmp8 & 0xe0) >>> 5;
    const cIndex = view.getUint8(9);
    const wFeedID = view.getUint16(10, true);
    const wProductID = view.getUint16(12, true);
    const cProdVendor = view.getUint8(14);
    const cProdType = view.getUint8(15);
    const dwProductValidTime = view.getUint32(16, true);
    const dwProductModelTime = view.getUint32(20, true);
    const cConfidence = view.getUint8(24);
    const cSeverity = view.getUint8(25);
    const wDistanceOff = view.getUint16(26, true);
    const wAltBase = view.getUint16(28, true);
    const wAltTop = view.getUint16(30, true);
    const dwRouteID = view.getUint32(32, true);
    const wStartPtIdx = view.getUint16(36, true);
    const wStartPtOff = view.getUint16(38, true);
    const wResetPtIdx = view.getUint16(40, true);
    const wResetPtOff = view.getUint16(42, true);
    const nStartLat100k = view.getInt32(44, true);
    const nStartLon100k = view.getInt32(48, true);
    const nEndLat100k = view.getInt32(52, true);
    const nEndLon100k = view.getInt32(56, true);
    const dwEnterTime = view.getUint32(60, true);
    const dwExitTime = view.getUint32(64, true);
    const dwCreateTime = view.getUint32(68, true);
    const dwRemoveTime = view.getUint32(72, true);
    const dwUpdTime = view.getUint32(76, true);
    const pcGuid = [
        view.getUint8(80),
        view.getUint8(81),
        view.getUint8(82),
        view.getUint8(83),
        view.getUint8(84),
        view.getUint8(85),
        view.getUint8(86),
        view.getUint8(87),
        view.getUint8(88),
        view.getUint8(89),
        view.getUint8(90),
        view.getUint8(91),
        view.getUint8(92),
        view.getUint8(93),
        view.getUint8(94),
        view.getUint8(95)
    ];
    // Total size: 96 bytes
    return {
        wEventCode,
        pcObjectID,
        cState,
        bUsed,
        bCompliant,
        bDetails,
        cClass,
        cIndex,
        wFeedID,
        wProductID,
        cProdVendor,
        cProdType,
        dwProductValidTime,
        dwProductModelTime,
        cConfidence,
        cSeverity,
        wDistanceOff,
        wAltBase,
        wAltTop,
        dwRouteID,
        wStartPtIdx,
        wStartPtOff,
        wResetPtIdx,
        wResetPtOff,
        nStartLat100k,
        nStartLon100k,
        nEndLat100k,
        nEndLon100k,
        dwEnterTime,
        dwExitTime,
        dwCreateTime,
        dwRemoveTime,
        dwUpdTime,
        pcGuid
    };
}

export interface EVENT_REC {
    wEventCode: number;
    cState: number;
    cIndex: number;
    dwTime: number;
}

export function parseEVENT_REC(buffer: ArrayBuffer): EVENT_REC {
    const view = new DataView(buffer);
    const wEventCode = view.getUint16(0, true);
    const cState = view.getUint8(2);
    const cIndex = view.getUint8(3);
    const dwTime = view.getUint32(4, true);
    // Total size: 8 bytes
    return {
        wEventCode,
        cState,
        cIndex,
        dwTime
    };
}

export interface EVENT_REC2 {
    wEventCode: number;
    pcObjectID: string;
    cState: 0 | 1 | 2;
    bUsed: number;
    bCompliant: number;
    bDetails: number;
    generatedName_11: number;
    cIndex: number;
    wFeedID: number;
    dwSetTime: number;
    dwResetTime: number;
    details?: string;
}

export function parseEVENT_REC2(buffer: ArrayBuffer): EVENT_REC2 {
    const view = new DataView(buffer);
    const wEventCode = view.getUint16(0, true);
    const chars_2 = [];
    for (let i = 0; i < 6; i++) {
        const char = view.getUint8(2 + i);
        if (char === 0) break;
        chars_2.push(String.fromCharCode(char));
    }
    const pcObjectID = chars_2.join('').trim();

    // Decoding bits 2,1,1,1,3
    const tmp8 = view.getUint8(8);
    const cState = tmp8 & 0x3;
    const bUsed = (tmp8 & 0x4) >>> 2;
    const bCompliant = (tmp8 & 0x8) >>> 3;
    const bDetails = (tmp8 & 0x10) >>> 4;
    const generatedName_11 = (tmp8 & 0xe0) >>> 5;
    const cIndex = view.getUint8(9);
    const wFeedID = view.getUint16(10, true);
    const dwSetTime = view.getUint32(12, true);
    const dwResetTime = view.getUint32(16, true);
    // Total size: 20 bytes
    return {
        wEventCode,
        pcObjectID,
        cState: cState as 0 | 1 | 2,
        bUsed,
        bCompliant,
        bDetails,
        generatedName_11,
        cIndex,
        wFeedID,
        dwSetTime,
        dwResetTime
    };
}

export interface Init1b {
    cMsgID: number;
    wRecordID: number;
    fAltitudeType: number;
    fAltitude2: number;
    fAltitudeChg: number;
    fACID0: number;
    fACID1: number;
    fACID2: number;
    fACID3: number;
    fACID4: number;
    fACID5: number;
    fACID6: number;
    wT1: number;
    fLatitude0: number;
    fHour: number;
    fMinute: number;
    fLongitude0: number;
    fAirspeed: number;
    fLatitude1: number;
    fStatus: number;
    fCenter: number;
    dwT1: number;
    fLongitude1: number;
    fAltitude: number;
    fBlocked: number;
}

// @ts-ignore
let cacheInit1b: Init1b = {};

export function parseInit1b(buffer: ArrayBuffer): Init1b {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,3,9,2
    const tmp1 = view.getUint32(1, true);
    const wRecordID = tmp1 & 0x3ffff;
    const fAltitudeType = (tmp1 & 0x1c0000) >>> 18;
    const fAltitude2 = (tmp1 & 0x3fe00000) >>> 21;
    const fAltitudeChg = (tmp1 & 0xc0000000) >>> 30;

    // Decoding bits 6,6,6,6,6,2
    const tmp5 = view.getUint32(5, true);
    const fACID0 = tmp5 & 0x3f;
    const fACID1 = (tmp5 & 0xfc0) >>> 6;
    const fACID2 = (tmp5 & 0x3f000) >>> 12;
    const fACID3 = (tmp5 & 0xfc0000) >>> 18;
    const fACID4 = (tmp5 & 0x3f000000) >>> 24;

    // Decoding bits 6,6,4
    const tmp9 = view.getUint16(9, true);
    const fACID5 = tmp9 & 0x3f;
    const fACID6 = (tmp9 & 0xfc0) >>> 6;
    const wT1 = (tmp9 & 0xf000) >>> 12;

    // Decoding bits 21,5,6
    const tmp11 = view.getUint32(11, true);
    const fLatitude0 = tmp11 & 0x1fffff;
    const fHour = (tmp11 & 0x3e00000) >>> 21;
    const fMinute = (tmp11 & 0xfc000000) >>> 26;

    // Decoding bits 22,10
    const tmp15 = view.getUint32(15, true);
    const fLongitude0 = tmp15 & 0x3fffff;
    const fAirspeed = (tmp15 & 0xffc00000) >>> 22;

    // Decoding bits 21,3,6,2
    const tmp19 = view.getUint32(19, true);
    const fLatitude1 = tmp19 & 0x1fffff;
    const fStatus = (tmp19 & 0xe00000) >>> 21;
    const fCenter = (tmp19 & 0x3f000000) >>> 24;
    const dwT1 = (tmp19 & 0xc0000000) >>> 30;

    // Decoding bits 22,9,1
    const tmp23 = view.getUint32(23, true);
    const fLongitude1 = tmp23 & 0x3fffff;
    const fAltitude = (tmp23 & 0x7fc00000) >>> 22;
    const fBlocked = (tmp23 & 0x80000000) >>> 31;
    // Total size: 27 bytes
    cacheInit1b.cMsgID = cMsgID;
    cacheInit1b.wRecordID = wRecordID;
    cacheInit1b.fAltitudeType = fAltitudeType;
    cacheInit1b.fAltitude2 = fAltitude2;
    cacheInit1b.fAltitudeChg = fAltitudeChg;
    cacheInit1b.fACID0 = fACID0;
    cacheInit1b.fACID1 = fACID1;
    cacheInit1b.fACID2 = fACID2;
    cacheInit1b.fACID3 = fACID3;
    cacheInit1b.fACID4 = fACID4;
    cacheInit1b.fACID5 = fACID5;
    cacheInit1b.fACID6 = fACID6;
    cacheInit1b.wT1 = wT1;
    cacheInit1b.fLatitude0 = fLatitude0;
    cacheInit1b.fHour = fHour;
    cacheInit1b.fMinute = fMinute;
    cacheInit1b.fLongitude0 = fLongitude0;
    cacheInit1b.fAirspeed = fAirspeed;
    cacheInit1b.fLatitude1 = fLatitude1;
    cacheInit1b.fStatus = fStatus;
    cacheInit1b.fCenter = fCenter;
    cacheInit1b.dwT1 = dwT1;
    cacheInit1b.fLongitude1 = fLongitude1;
    cacheInit1b.fAltitude = fAltitude;
    cacheInit1b.fBlocked = fBlocked;
    return cacheInit1b;
}

export interface Init1 {
    cMsgID: number;
    wRecordID: number;
    fACID0: number;
    fACID1: number;
    fACID2: number;
    fACID3: number;
    fACID4: number;
    fAltitudeChg: number;
    fACID5: number;
    fAirspeed: number;
    fAltitudeType: number;
    fAltitude2: number;
    fStatus: number;
    fBlocked: number;
    fACID6: number;
    fAltitude: number;
    fHour: number;
    fMinute: number;
    fCenter: number;
    psPosition: GEO_COORD_INT[];
}

// @ts-ignore
let cacheInit1: Init1 = {};

export function parseInit1(buffer: ArrayBuffer): Init1 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wRecordID = view.getUint16(1, true);

    // Decoding bits 6,6,6,6,6,2
    const tmp3 = view.getUint32(3, true);
    const fACID0 = tmp3 & 0x3f;
    const fACID1 = (tmp3 & 0xfc0) >>> 6;
    const fACID2 = (tmp3 & 0x3f000) >>> 12;
    const fACID3 = (tmp3 & 0xfc0000) >>> 18;
    const fACID4 = (tmp3 & 0x3f000000) >>> 24;
    const fAltitudeChg = (tmp3 & 0xc0000000) >>> 30;

    // Decoding bits 6,10
    const tmp7 = view.getUint16(7, true);
    const fACID5 = tmp7 & 0x3f;
    const fAirspeed = (tmp7 & 0xffc0) >>> 6;

    // Decoding bits 3,9,3,1
    const tmp9 = view.getUint16(9, true);
    const fAltitudeType = tmp9 & 0x7;
    const fAltitude2 = (tmp9 & 0xff8) >>> 3;
    const fStatus = (tmp9 & 0x7000) >>> 12;
    const fBlocked = (tmp9 & 0x8000) >>> 15;

    // Decoding bits 6,9,5,6,6
    const tmp11 = view.getUint32(11, true);
    const fACID6 = tmp11 & 0x3f;
    const fAltitude = (tmp11 & 0x7fc0) >>> 6;
    const fHour = (tmp11 & 0xf8000) >>> 15;
    const fMinute = (tmp11 & 0x3f00000) >>> 20;
    const fCenter = (tmp11 & 0xfc000000) >>> 26;
    const psPosition = [parseGEO_COORD_INT(buffer.slice(15)), parseGEO_COORD_INT(buffer.slice(20))];
    // Total size: 25 bytes
    cacheInit1.cMsgID = cMsgID;
    cacheInit1.wRecordID = wRecordID;
    cacheInit1.fACID0 = fACID0;
    cacheInit1.fACID1 = fACID1;
    cacheInit1.fACID2 = fACID2;
    cacheInit1.fACID3 = fACID3;
    cacheInit1.fACID4 = fACID4;
    cacheInit1.fAltitudeChg = fAltitudeChg;
    cacheInit1.fACID5 = fACID5;
    cacheInit1.fAirspeed = fAirspeed;
    cacheInit1.fAltitudeType = fAltitudeType;
    cacheInit1.fAltitude2 = fAltitude2;
    cacheInit1.fStatus = fStatus;
    cacheInit1.fBlocked = fBlocked;
    cacheInit1.fACID6 = fACID6;
    cacheInit1.fAltitude = fAltitude;
    cacheInit1.fHour = fHour;
    cacheInit1.fMinute = fMinute;
    cacheInit1.fCenter = fCenter;
    cacheInit1.psPosition = psPosition;
    return cacheInit1;
}

export interface Init2d {
    cMsgID: number;
    wRecordID: number;
    fSpeed1: number;
    fAltitudeType1: number;
    fwDiverting: number;
    fOrigin0: number;
    fOrigin1: number;
    fOrigin2: number;
    fOrigin3: number;
    fDestination0: number;
    fFPSeq: number;
    fDestination1: number;
    fDestination2: number;
    fDestination3: number;
    fAircraftType0: number;
    fAircraftType1: number;
    fAltitude2MSB: number;
    fAltitudeMSB: number;
    fAircraftType2: number;
    fAircraftType3: number;
    fDepHour: number;
    fDepMinute: number;
    fArrHour: number;
    fHeavy: number;
    fTCAS: number;
    fArrMinute: number;
    fDelayed: number;
    fOnGround: number;
    pcLastDest0: number;
    pcLastDest1: number;
    pcLastDest2: number;
    pcLastDest3: number;
    fwFeedID0: number;
    fiLastRepLat: number;
    fiLastRepLon: number;
    fAltitude1: number;
    fwHolding: number;
    fwFeedID1: number;
    fwLastRepFeedID: number;
    fwMilesOffRoute: number;
    fSpeedType1: number;
    fwFEETAHour: number;
    fwFEETAMinute: number;
    fwSecond0: number;
    fwMinute1: number;
    fwSecond1: number;
    fwIsProjected0: number;
    fwHour1: number;
    fwIsProjected1: number;
    fwDepDay: number;
    fwArrDay: number;
    fSpeedType0: number;
    fwLastRepDay: number;
    fwLastRepHour: number;
    fwLastRepMin: number;
    fwObjectClass: number;
    fwT1: number;
}

// @ts-ignore
let cacheInit2d: Init2d = {};

export function parseInit2d(buffer: ArrayBuffer): Init2d {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,10,3,1
    const tmp1 = view.getUint32(1, true);
    const wRecordID = tmp1 & 0x3ffff;
    const fSpeed1 = (tmp1 & 0xffc0000) >>> 18;
    const fAltitudeType1 = (tmp1 & 0x70000000) >>> 28;
    const fwDiverting = (tmp1 & 0x80000000) >>> 31;

    // Decoding bits 6,6,6,6,6,2
    const tmp5 = view.getUint32(5, true);
    const fOrigin0 = tmp5 & 0x3f;
    const fOrigin1 = (tmp5 & 0xfc0) >>> 6;
    const fOrigin2 = (tmp5 & 0x3f000) >>> 12;
    const fOrigin3 = (tmp5 & 0xfc0000) >>> 18;
    const fDestination0 = (tmp5 & 0x3f000000) >>> 24;
    const fFPSeq = (tmp5 & 0xc0000000) >>> 30;

    // Decoding bits 6,6,6,6,6,1,1
    const tmp9 = view.getUint32(9, true);
    const fDestination1 = tmp9 & 0x3f;
    const fDestination2 = (tmp9 & 0xfc0) >>> 6;
    const fDestination3 = (tmp9 & 0x3f000) >>> 12;
    const fAircraftType0 = (tmp9 & 0xfc0000) >>> 18;
    const fAircraftType1 = (tmp9 & 0x3f000000) >>> 24;
    const fAltitude2MSB = (tmp9 & 0x40000000) >>> 30;
    const fAltitudeMSB = (tmp9 & 0x80000000) >>> 31;

    // Decoding bits 6,6,5,6,5,2,2
    const tmp13 = view.getUint32(13, true);
    const fAircraftType2 = tmp13 & 0x3f;
    const fAircraftType3 = (tmp13 & 0xfc0) >>> 6;
    const fDepHour = (tmp13 & 0x1f000) >>> 12;
    const fDepMinute = (tmp13 & 0x7e0000) >>> 17;
    const fArrHour = (tmp13 & 0xf800000) >>> 23;
    const fHeavy = (tmp13 & 0x30000000) >>> 28;
    const fTCAS = (tmp13 & 0xc0000000) >>> 30;

    // Decoding bits 6,1,1,6,6,6,6
    const tmp17 = view.getUint32(17, true);
    const fArrMinute = tmp17 & 0x3f;
    const fDelayed = (tmp17 & 0x40) >>> 6;
    const fOnGround = (tmp17 & 0x80) >>> 7;
    const pcLastDest0 = (tmp17 & 0x3f00) >>> 8;
    const pcLastDest1 = (tmp17 & 0xfc000) >>> 14;
    const pcLastDest2 = (tmp17 & 0x3f00000) >>> 20;
    const pcLastDest3 = (tmp17 & 0xfc000000) >>> 26;

    // Decoding bits 11,21
    const tmp21 = view.getUint32(21, true);
    const fwFeedID0 = tmp21 & 0x7ff;
    const fiLastRepLat = (tmp21 & 0xfffff800) >>> 11;

    // Decoding bits 22,9,1
    const tmp25 = view.getUint32(25, true);
    const fiLastRepLon = tmp25 & 0x3fffff;
    const fAltitude1 = (tmp25 & 0x7fc00000) >>> 22;
    const fwHolding = (tmp25 & 0x80000000) >>> 31;

    // Decoding bits 11,11,10
    const tmp29 = view.getUint32(29, true);
    const fwFeedID1 = tmp29 & 0x7ff;
    const fwLastRepFeedID = (tmp29 & 0x3ff800) >>> 11;
    const fwMilesOffRoute = (tmp29 & 0xffc00000) >>> 22;

    // Decoding bits 2,5,6,6,6,6,1
    const tmp33 = view.getUint32(33, true);
    const fSpeedType1 = tmp33 & 0x3;
    const fwFEETAHour = (tmp33 & 0x7c) >>> 2;
    const fwFEETAMinute = (tmp33 & 0x1f80) >>> 7;
    const fwSecond0 = (tmp33 & 0x7e000) >>> 13;
    const fwMinute1 = (tmp33 & 0x1f80000) >>> 19;
    const fwSecond1 = (tmp33 & 0x7e000000) >>> 25;
    const fwIsProjected0 = (tmp33 & 0x80000000) >>> 31;

    // Decoding bits 5,1,3,3,2,3,5,6,2,2
    const tmp37 = view.getUint32(37, true);
    const fwHour1 = tmp37 & 0x1f;
    const fwIsProjected1 = (tmp37 & 0x20) >>> 5;
    const fwDepDay = (tmp37 & 0x1c0) >>> 6;
    const fwArrDay = (tmp37 & 0xe00) >>> 9;
    const fSpeedType0 = (tmp37 & 0x3000) >>> 12;
    const fwLastRepDay = (tmp37 & 0x1c000) >>> 14;
    const fwLastRepHour = (tmp37 & 0x3e0000) >>> 17;
    const fwLastRepMin = (tmp37 & 0xfc00000) >>> 22;
    const fwObjectClass = (tmp37 & 0x30000000) >>> 28;
    const fwT1 = (tmp37 & 0xc0000000) >>> 30;
    // Total size: 41 bytes
    cacheInit2d.cMsgID = cMsgID;
    cacheInit2d.wRecordID = wRecordID;
    cacheInit2d.fSpeed1 = fSpeed1;
    cacheInit2d.fAltitudeType1 = fAltitudeType1;
    cacheInit2d.fwDiverting = fwDiverting;
    cacheInit2d.fOrigin0 = fOrigin0;
    cacheInit2d.fOrigin1 = fOrigin1;
    cacheInit2d.fOrigin2 = fOrigin2;
    cacheInit2d.fOrigin3 = fOrigin3;
    cacheInit2d.fDestination0 = fDestination0;
    cacheInit2d.fFPSeq = fFPSeq;
    cacheInit2d.fDestination1 = fDestination1;
    cacheInit2d.fDestination2 = fDestination2;
    cacheInit2d.fDestination3 = fDestination3;
    cacheInit2d.fAircraftType0 = fAircraftType0;
    cacheInit2d.fAircraftType1 = fAircraftType1;
    cacheInit2d.fAltitude2MSB = fAltitude2MSB;
    cacheInit2d.fAltitudeMSB = fAltitudeMSB;
    cacheInit2d.fAircraftType2 = fAircraftType2;
    cacheInit2d.fAircraftType3 = fAircraftType3;
    cacheInit2d.fDepHour = fDepHour;
    cacheInit2d.fDepMinute = fDepMinute;
    cacheInit2d.fArrHour = fArrHour;
    cacheInit2d.fHeavy = fHeavy;
    cacheInit2d.fTCAS = fTCAS;
    cacheInit2d.fArrMinute = fArrMinute;
    cacheInit2d.fDelayed = fDelayed;
    cacheInit2d.fOnGround = fOnGround;
    cacheInit2d.pcLastDest0 = pcLastDest0;
    cacheInit2d.pcLastDest1 = pcLastDest1;
    cacheInit2d.pcLastDest2 = pcLastDest2;
    cacheInit2d.pcLastDest3 = pcLastDest3;
    cacheInit2d.fwFeedID0 = fwFeedID0;
    cacheInit2d.fiLastRepLat = fiLastRepLat;
    cacheInit2d.fiLastRepLon = fiLastRepLon;
    cacheInit2d.fAltitude1 = fAltitude1;
    cacheInit2d.fwHolding = fwHolding;
    cacheInit2d.fwFeedID1 = fwFeedID1;
    cacheInit2d.fwLastRepFeedID = fwLastRepFeedID;
    cacheInit2d.fwMilesOffRoute = fwMilesOffRoute;
    cacheInit2d.fSpeedType1 = fSpeedType1;
    cacheInit2d.fwFEETAHour = fwFEETAHour;
    cacheInit2d.fwFEETAMinute = fwFEETAMinute;
    cacheInit2d.fwSecond0 = fwSecond0;
    cacheInit2d.fwMinute1 = fwMinute1;
    cacheInit2d.fwSecond1 = fwSecond1;
    cacheInit2d.fwIsProjected0 = fwIsProjected0;
    cacheInit2d.fwHour1 = fwHour1;
    cacheInit2d.fwIsProjected1 = fwIsProjected1;
    cacheInit2d.fwDepDay = fwDepDay;
    cacheInit2d.fwArrDay = fwArrDay;
    cacheInit2d.fSpeedType0 = fSpeedType0;
    cacheInit2d.fwLastRepDay = fwLastRepDay;
    cacheInit2d.fwLastRepHour = fwLastRepHour;
    cacheInit2d.fwLastRepMin = fwLastRepMin;
    cacheInit2d.fwObjectClass = fwObjectClass;
    cacheInit2d.fwT1 = fwT1;
    return cacheInit2d;
}

export interface UpdateTZ3 {
    cMsgID: number;
    fwRecordID: number;
    fwOnGround: number;
    fwDiverting: number;
    fwMinute: number;
    fwSecond: number;
    fiLatitude: number;
    fwFeedID: number;
    fiLongitude: number;
    fwSpeed: number;
    fwAltitude: number;
    fwAltitudeType: number;
    fwAltitude2: number;
    fwAltitudeChg: number;
    fwSpeedType: number;
    fwHour: number;
    fwMilesOffRoute: number;
    fwFEETAMinute: number;
    fwFEETAHour: number;
    fwHolding: number;
    fwIsProjected: number;
    fwDidGoAround: number;
}

// @ts-ignore
let cacheUpdateTZ3: UpdateTZ3 = {};

export function parseUpdateTZ3(buffer: ArrayBuffer): UpdateTZ3 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,1,1,6,6
    const tmp1 = view.getUint32(1, true);
    const fwRecordID = tmp1 & 0x3ffff;
    const fwOnGround = (tmp1 & 0x40000) >>> 18;
    const fwDiverting = (tmp1 & 0x80000) >>> 19;
    const fwMinute = (tmp1 & 0x3f00000) >>> 20;
    const fwSecond = (tmp1 & 0xfc000000) >>> 26;

    // Decoding bits 21,11
    const tmp5 = view.getUint32(5, true);
    const fiLatitude = tmp5 & 0x1fffff;
    const fwFeedID = (tmp5 & 0xffe00000) >>> 21;

    // Decoding bits 22,10
    const tmp9 = view.getUint32(9, true);
    const fiLongitude = tmp9 & 0x3fffff;
    const fwSpeed = (tmp9 & 0xffc00000) >>> 22;

    // Decoding bits 10,3,10,2,2,5
    const tmp13 = view.getUint32(13, true);
    const fwAltitude = tmp13 & 0x3ff;
    const fwAltitudeType = (tmp13 & 0x1c00) >>> 10;
    const fwAltitude2 = (tmp13 & 0x7fe000) >>> 13;
    const fwAltitudeChg = (tmp13 & 0x1800000) >>> 23;
    const fwSpeedType = (tmp13 & 0x6000000) >>> 25;
    const fwHour = (tmp13 & 0xf8000000) >>> 27;

    // Decoding bits 10,6
    const tmp17 = view.getUint16(17, true);
    const fwMilesOffRoute = tmp17 & 0x3ff;
    const fwFEETAMinute = (tmp17 & 0xfc00) >>> 10;

    // Decoding bits 5,1,1,1
    const tmp19 = view.getUint8(19);
    const fwFEETAHour = tmp19 & 0x1f;
    const fwHolding = (tmp19 & 0x20) >>> 5;
    const fwIsProjected = (tmp19 & 0x40) >>> 6;
    const fwDidGoAround = (tmp19 & 0x80) >>> 7;
    // Total size: 20 bytes
    cacheUpdateTZ3.cMsgID = cMsgID;
    cacheUpdateTZ3.fwRecordID = fwRecordID;
    cacheUpdateTZ3.fwOnGround = fwOnGround;
    cacheUpdateTZ3.fwDiverting = fwDiverting;
    cacheUpdateTZ3.fwMinute = fwMinute;
    cacheUpdateTZ3.fwSecond = fwSecond;
    cacheUpdateTZ3.fiLatitude = fiLatitude;
    cacheUpdateTZ3.fwFeedID = fwFeedID;
    cacheUpdateTZ3.fiLongitude = fiLongitude;
    cacheUpdateTZ3.fwSpeed = fwSpeed;
    cacheUpdateTZ3.fwAltitude = fwAltitude;
    cacheUpdateTZ3.fwAltitudeType = fwAltitudeType;
    cacheUpdateTZ3.fwAltitude2 = fwAltitude2;
    cacheUpdateTZ3.fwAltitudeChg = fwAltitudeChg;
    cacheUpdateTZ3.fwSpeedType = fwSpeedType;
    cacheUpdateTZ3.fwHour = fwHour;
    cacheUpdateTZ3.fwMilesOffRoute = fwMilesOffRoute;
    cacheUpdateTZ3.fwFEETAMinute = fwFEETAMinute;
    cacheUpdateTZ3.fwFEETAHour = fwFEETAHour;
    cacheUpdateTZ3.fwHolding = fwHolding;
    cacheUpdateTZ3.fwIsProjected = fwIsProjected;
    cacheUpdateTZ3.fwDidGoAround = fwDidGoAround;
    return cacheUpdateTZ3;
}

export interface UpdateTZsfc1 {
    cMsgID: number;
    fwRecordID: number;
    fwHeading: number;
    fwTimeOff: number;
    fwT1: number;
    fiLatitude: number;
    fiLongitude: number;
    fwFeedIDsfc: number;
}

// @ts-ignore
let cacheUpdateTZsfc1: UpdateTZsfc1 = {};

export function parseUpdateTZsfc1(buffer: ArrayBuffer): UpdateTZsfc1 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,9,4,1
    const tmp1 = view.getUint32(1, true);
    const fwRecordID = tmp1 & 0x3ffff;
    const fwHeading = (tmp1 & 0x7fc0000) >>> 18;
    const fwTimeOff = (tmp1 & 0x78000000) >>> 27;
    const fwT1 = (tmp1 & 0x80000000) >>> 31;

    // Decoding bits 13,13,6
    const tmp5 = view.getUint32(5, true);
    const fiLatitude = tmp5 & 0x1fff;
    const fiLongitude = (tmp5 & 0x3ffe000) >>> 13;
    const fwFeedIDsfc = (tmp5 & 0xfc000000) >>> 26;
    // Total size: 9 bytes
    cacheUpdateTZsfc1.cMsgID = cMsgID;
    cacheUpdateTZsfc1.fwRecordID = fwRecordID;
    cacheUpdateTZsfc1.fwHeading = fwHeading;
    cacheUpdateTZsfc1.fwTimeOff = fwTimeOff;
    cacheUpdateTZsfc1.fwT1 = fwT1;
    cacheUpdateTZsfc1.fiLatitude = fiLatitude;
    cacheUpdateTZsfc1.fiLongitude = fiLongitude;
    cacheUpdateTZsfc1.fwFeedIDsfc = fwFeedIDsfc;
    return cacheUpdateTZsfc1;
}

export interface UpdateTZsfc2 {
    cMsgID: number;
    fwRecordID: number;
    fwSpeed: number;
    fwTimeOff: number;
    fiLatitude: number;
    fiLongitude: number;
    fwFeedIDsfc: number;
    fwAltitude: number;
    fwT1: number;
}

// @ts-ignore
let cacheUpdateTZsfc2: UpdateTZsfc2 = {};

export function parseUpdateTZsfc2(buffer: ArrayBuffer): UpdateTZsfc2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,10,4
    const tmp1 = view.getUint32(1, true);
    const fwRecordID = tmp1 & 0x3ffff;
    const fwSpeed = (tmp1 & 0xffc0000) >>> 18;
    const fwTimeOff = (tmp1 & 0xf0000000) >>> 28;

    // Decoding bits 16,16
    const tmp5 = view.getUint32(5, true);
    const fiLatitude = tmp5 & 0xffff;
    const fiLongitude = (tmp5 & 0xffff0000) >>> 16;

    // Decoding bits 6,8,2
    const tmp9 = view.getUint16(9, true);
    const fwFeedIDsfc = tmp9 & 0x3f;
    const fwAltitude = (tmp9 & 0x3fc0) >>> 6;
    const fwT1 = (tmp9 & 0xc000) >>> 14;
    // Total size: 11 bytes
    cacheUpdateTZsfc2.cMsgID = cMsgID;
    cacheUpdateTZsfc2.fwRecordID = fwRecordID;
    cacheUpdateTZsfc2.fwSpeed = fwSpeed;
    cacheUpdateTZsfc2.fwTimeOff = fwTimeOff;
    cacheUpdateTZsfc2.fiLatitude = fiLatitude;
    cacheUpdateTZsfc2.fiLongitude = fiLongitude;
    cacheUpdateTZsfc2.fwFeedIDsfc = fwFeedIDsfc;
    cacheUpdateTZsfc2.fwAltitude = fwAltitude;
    cacheUpdateTZsfc2.fwT1 = fwT1;
    return cacheUpdateTZsfc2;
}

export interface UpdateSfcLow {
    cMsgID: number;
    fwRecordID: number;
    pcRunwayID: string;
    pcPairedFixID: string;
    fwICAOID: number;
}

const cacheUpdateSfcLow: UpdateSfcLow = {} as UpdateSfcLow;
export function parseUpdateSfcLow(buffer: ArrayBuffer): UpdateSfcLow {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    const tmp1 = view.getUint32(2, true);
    const fwRecordID = tmp1 & 0x3ffff;

    const runway = [view.getUint8(6), view.getUint8(7), view.getUint8(8)]
        .map((x) => String.fromCharCode(x))
        .join('')
        .trim();

    const fix = [view.getUint8(9), view.getUint8(10), view.getUint8(11)]
        .map((x) => String.fromCharCode(x))
        .join('')
        .trim();

    const icao = view.getUint32(12, true) & 0xffffff;
    // Total size: 11 bytes
    cacheUpdateSfcLow.cMsgID = cMsgID;
    cacheUpdateSfcLow.fwRecordID = fwRecordID;
    cacheUpdateSfcLow.pcPairedFixID = fix;
    cacheUpdateSfcLow.pcRunwayID = runway;
    cacheUpdateSfcLow.fwICAOID = icao;

    return cacheUpdateSfcLow;
}

export interface UpdateADSBMed {
    cMsgID: number;
    cLength: number;
    fwRecordID: number;
    fwBeaconCode: number;
    fwT1: number;
}

// @ts-ignore
let cacheUpdateADSBMed: UpdateADSBMed = {};

export function parseUpdateADSBMed(buffer: ArrayBuffer): UpdateADSBMed {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const cLength = view.getUint8(1);

    // Decoding bits 18,13,1
    const tmp2 = view.getUint32(2, true);
    const fwRecordID = tmp2 & 0x3ffff;
    const fwBeaconCode = (tmp2 & 0x7ffc0000) >>> 18;
    const fwT1 = (tmp2 & 0x80000000) >>> 31;
    // Total size: 6 bytes
    cacheUpdateADSBMed.cMsgID = cMsgID;
    cacheUpdateADSBMed.cLength = cLength;
    cacheUpdateADSBMed.fwRecordID = fwRecordID;
    cacheUpdateADSBMed.fwBeaconCode = fwBeaconCode;
    cacheUpdateADSBMed.fwT1 = fwT1;
    return cacheUpdateADSBMed;
}

export interface UpdateADSBLow {
    cMsgID: number;
    cLength: number;
    fwRecordID: number;
    fwT1: number;
    pcRegNum: string;
    fwICAOID: number;
    fwT2: number;
}

// @ts-ignore
let cacheUpdateADSBLow: UpdateADSBLow = {};

export function parseUpdateADSBLow(buffer: ArrayBuffer): UpdateADSBLow {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const cLength = view.getUint8(1);

    // Decoding bits 18,14
    const tmp2 = view.getUint32(2, true);
    const fwRecordID = tmp2 & 0x3ffff;
    const fwT1 = (tmp2 & 0xfffc0000) >>> 18;
    const chars_6 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(6 + i);
        if (char === 0) break;
        chars_6.push(String.fromCharCode(char));
    }
    const pcRegNum = chars_6.join('').trim();

    // Decoding bits 24,8
    const tmp13 = view.getUint32(13, true);
    const fwICAOID = tmp13 & 0xffffff;
    const fwT2 = (tmp13 & 0xff000000) >>> 24;
    // Total size: 17 bytes
    cacheUpdateADSBLow.cMsgID = cMsgID;
    cacheUpdateADSBLow.cLength = cLength;
    cacheUpdateADSBLow.fwRecordID = fwRecordID;
    cacheUpdateADSBLow.fwT1 = fwT1;
    cacheUpdateADSBLow.pcRegNum = pcRegNum;
    cacheUpdateADSBLow.fwICAOID = fwICAOID;
    cacheUpdateADSBLow.fwT2 = fwT2;
    return cacheUpdateADSBLow;
}

export interface UpdateDZb {
    cMsgID: number;
    wRecordID: number;
    fOrigin0: number;
    fOrigin1: number;
    fOrigin2: number;
    fOrigin3: number;
    fDestination0: number;
    fFPSeq: number;
    fDestination1: number;
    fDestination2: number;
    fDestination3: number;
    fAircraftType0: number;
    fAircraftType1: number;
    fHeavy: number;
    fAircraftType2: number;
    fAircraftType3: number;
    fDepMinute: number;
    fArrMinute: number;
    fHour: number;
    fStatus: number;
    fDepHour: number;
    fArrHour: number;
    fMinute: number;
    fCenter: number;
    fTCAS: number;
    fDepDay: number;
    fArrDay: number;
    wRecordIDmsb: number;
}

// @ts-ignore
let cacheUpdateDZb: UpdateDZb = {};

export function parseUpdateDZb(buffer: ArrayBuffer): UpdateDZb {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wRecordID = view.getUint16(1, true);

    // Decoding bits 6,6,6,6,6,2
    const tmp3 = view.getUint32(3, true);
    const fOrigin0 = tmp3 & 0x3f;
    const fOrigin1 = (tmp3 & 0xfc0) >>> 6;
    const fOrigin2 = (tmp3 & 0x3f000) >>> 12;
    const fOrigin3 = (tmp3 & 0xfc0000) >>> 18;
    const fDestination0 = (tmp3 & 0x3f000000) >>> 24;
    const fFPSeq = (tmp3 & 0xc0000000) >>> 30;

    // Decoding bits 6,6,6,6,6,2
    const tmp7 = view.getUint32(7, true);
    const fDestination1 = tmp7 & 0x3f;
    const fDestination2 = (tmp7 & 0xfc0) >>> 6;
    const fDestination3 = (tmp7 & 0x3f000) >>> 12;
    const fAircraftType0 = (tmp7 & 0xfc0000) >>> 18;
    const fAircraftType1 = (tmp7 & 0x3f000000) >>> 24;
    const fHeavy = (tmp7 & 0xc0000000) >>> 30;

    // Decoding bits 6,6,6,6,5,3
    const tmp11 = view.getUint32(11, true);
    const fAircraftType2 = tmp11 & 0x3f;
    const fAircraftType3 = (tmp11 & 0xfc0) >>> 6;
    const fDepMinute = (tmp11 & 0x3f000) >>> 12;
    const fArrMinute = (tmp11 & 0xfc0000) >>> 18;
    const fHour = (tmp11 & 0x1f000000) >>> 24;
    const fStatus = (tmp11 & 0xe0000000) >>> 29;

    // Decoding bits 5,5,6,6,2,3,3,2
    const tmp15 = view.getUint32(15, true);
    const fDepHour = tmp15 & 0x1f;
    const fArrHour = (tmp15 & 0x3e0) >>> 5;
    const fMinute = (tmp15 & 0xfc00) >>> 10;
    const fCenter = (tmp15 & 0x3f0000) >>> 16;
    const fTCAS = (tmp15 & 0xc00000) >>> 22;
    const fDepDay = (tmp15 & 0x7000000) >>> 24;
    const fArrDay = (tmp15 & 0x38000000) >>> 27;
    const wRecordIDmsb = (tmp15 & 0xc0000000) >>> 30;
    // Total size: 19 bytes
    cacheUpdateDZb.cMsgID = cMsgID;
    cacheUpdateDZb.wRecordID = wRecordID;
    cacheUpdateDZb.fOrigin0 = fOrigin0;
    cacheUpdateDZb.fOrigin1 = fOrigin1;
    cacheUpdateDZb.fOrigin2 = fOrigin2;
    cacheUpdateDZb.fOrigin3 = fOrigin3;
    cacheUpdateDZb.fDestination0 = fDestination0;
    cacheUpdateDZb.fFPSeq = fFPSeq;
    cacheUpdateDZb.fDestination1 = fDestination1;
    cacheUpdateDZb.fDestination2 = fDestination2;
    cacheUpdateDZb.fDestination3 = fDestination3;
    cacheUpdateDZb.fAircraftType0 = fAircraftType0;
    cacheUpdateDZb.fAircraftType1 = fAircraftType1;
    cacheUpdateDZb.fHeavy = fHeavy;
    cacheUpdateDZb.fAircraftType2 = fAircraftType2;
    cacheUpdateDZb.fAircraftType3 = fAircraftType3;
    cacheUpdateDZb.fDepMinute = fDepMinute;
    cacheUpdateDZb.fArrMinute = fArrMinute;
    cacheUpdateDZb.fHour = fHour;
    cacheUpdateDZb.fStatus = fStatus;
    cacheUpdateDZb.fDepHour = fDepHour;
    cacheUpdateDZb.fArrHour = fArrHour;
    cacheUpdateDZb.fMinute = fMinute;
    cacheUpdateDZb.fCenter = fCenter;
    cacheUpdateDZb.fTCAS = fTCAS;
    cacheUpdateDZb.fDepDay = fDepDay;
    cacheUpdateDZb.fArrDay = fArrDay;
    cacheUpdateDZb.wRecordIDmsb = wRecordIDmsb;
    return cacheUpdateDZb;
}

export interface NewPlane {
    cMsgID: number;
    wRecordID: number;
    fACID0: number;
    fACID1: number;
    fACID2: number;
    fACID3: number;
    fACID4: number;
    fBlocked: number;
    fDelayed: number;
    fACID5: number;
    fACID6: number;
    fwObjectClass: number;
    fT1: number;
}

// @ts-ignore
let cacheNewPlane: NewPlane = {};

export function parseNewPlane(buffer: ArrayBuffer): NewPlane {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wRecordID = view.getUint16(1, true);

    // Decoding bits 6,6,6,6,6,1,1
    const tmp3 = view.getUint32(3, true);
    const fACID0 = tmp3 & 0x3f;
    const fACID1 = (tmp3 & 0xfc0) >>> 6;
    const fACID2 = (tmp3 & 0x3f000) >>> 12;
    const fACID3 = (tmp3 & 0xfc0000) >>> 18;
    const fACID4 = (tmp3 & 0x3f000000) >>> 24;
    const fBlocked = (tmp3 & 0x40000000) >>> 30;
    const fDelayed = (tmp3 & 0x80000000) >>> 31;

    // Decoding bits 6,6,2,2
    const tmp7 = view.getUint16(7, true);
    const fACID5 = tmp7 & 0x3f;
    const fACID6 = (tmp7 & 0xfc0) >>> 6;
    const fwObjectClass = (tmp7 & 0x3000) >>> 12;
    const fT1 = (tmp7 & 0xc000) >>> 14;
    // Total size: 9 bytes
    cacheNewPlane.cMsgID = cMsgID;
    cacheNewPlane.wRecordID = wRecordID;
    cacheNewPlane.fACID0 = fACID0;
    cacheNewPlane.fACID1 = fACID1;
    cacheNewPlane.fACID2 = fACID2;
    cacheNewPlane.fACID3 = fACID3;
    cacheNewPlane.fACID4 = fACID4;
    cacheNewPlane.fBlocked = fBlocked;
    cacheNewPlane.fDelayed = fDelayed;
    cacheNewPlane.fACID5 = fACID5;
    cacheNewPlane.fACID6 = fACID6;
    cacheNewPlane.fwObjectClass = fwObjectClass;
    cacheNewPlane.fT1 = fT1;
    return cacheNewPlane;
}

export interface NewPlaneEx {
    cMsgID: number;
    wRecordID: number;
    fACID0: number;
    fACID1: number;
    fACID2: number;
    fACID3: number;
    fACID4: number;
    fBlocked: number;
    fDelayed: number;
    fACID5: number;
    fACID6: number;
    wSrvRecMSB: number;
    fwObjectClass: number;
    wSrvRec: number;
}

// @ts-ignore
let cacheNewPlaneEx: NewPlaneEx = {};

export function parseNewPlaneEx(buffer: ArrayBuffer): NewPlaneEx {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wRecordID = view.getUint16(1, true);

    // Decoding bits 6,6,6,6,6,1,1
    const tmp3 = view.getUint32(3, true);
    const fACID0 = tmp3 & 0x3f;
    const fACID1 = (tmp3 & 0xfc0) >>> 6;
    const fACID2 = (tmp3 & 0x3f000) >>> 12;
    const fACID3 = (tmp3 & 0xfc0000) >>> 18;
    const fACID4 = (tmp3 & 0x3f000000) >>> 24;
    const fBlocked = (tmp3 & 0x40000000) >>> 30;
    const fDelayed = (tmp3 & 0x80000000) >>> 31;

    // Decoding bits 6,6,2,2
    const tmp7 = view.getUint16(7, true);
    const fACID5 = tmp7 & 0x3f;
    const fACID6 = (tmp7 & 0xfc0) >>> 6;
    const wSrvRecMSB = (tmp7 & 0x3000) >>> 12;
    const fwObjectClass = (tmp7 & 0xc000) >>> 14;
    const wSrvRec = view.getUint16(9, true);
    // Total size: 11 bytes
    cacheNewPlaneEx.cMsgID = cMsgID;
    cacheNewPlaneEx.wRecordID = wRecordID;
    cacheNewPlaneEx.fACID0 = fACID0;
    cacheNewPlaneEx.fACID1 = fACID1;
    cacheNewPlaneEx.fACID2 = fACID2;
    cacheNewPlaneEx.fACID3 = fACID3;
    cacheNewPlaneEx.fACID4 = fACID4;
    cacheNewPlaneEx.fBlocked = fBlocked;
    cacheNewPlaneEx.fDelayed = fDelayed;
    cacheNewPlaneEx.fACID5 = fACID5;
    cacheNewPlaneEx.fACID6 = fACID6;
    cacheNewPlaneEx.wSrvRecMSB = wSrvRecMSB;
    cacheNewPlaneEx.fwObjectClass = fwObjectClass;
    cacheNewPlaneEx.wSrvRec = wSrvRec;
    return cacheNewPlaneEx;
}

export interface UpdateACID {
    cMsgID: number;
    fwRecordID: number;
    fACID0: number;
    fACID1: number;
    fwPreflight: number;
    fwSource: number;
    fACID2: number;
    fACID3: number;
    fACID4: number;
    fACID5: number;
    fACID6: number;
    fTemp2: number;
}

// @ts-ignore
let cacheUpdateACID: UpdateACID = {};

export function parseUpdateACID(buffer: ArrayBuffer): UpdateACID {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,6,6,1,1
    const tmp1 = view.getUint32(1, true);
    const fwRecordID = tmp1 & 0x3ffff;
    const fACID0 = (tmp1 & 0xfc0000) >>> 18;
    const fACID1 = (tmp1 & 0x3f000000) >>> 24;
    const fwPreflight = (tmp1 & 0x40000000) >>> 30;
    const fwSource = (tmp1 & 0x80000000) >>> 31;

    // Decoding bits 6,6,6,6,6,2
    const tmp5 = view.getUint32(5, true);
    const fACID2 = tmp5 & 0x3f;
    const fACID3 = (tmp5 & 0xfc0) >>> 6;
    const fACID4 = (tmp5 & 0x3f000) >>> 12;
    const fACID5 = (tmp5 & 0xfc0000) >>> 18;
    const fACID6 = (tmp5 & 0x3f000000) >>> 24;
    const fTemp2 = (tmp5 & 0xc0000000) >>> 30;
    // Total size: 9 bytes
    cacheUpdateACID.cMsgID = cMsgID;
    cacheUpdateACID.fwRecordID = fwRecordID;
    cacheUpdateACID.fACID0 = fACID0;
    cacheUpdateACID.fACID1 = fACID1;
    cacheUpdateACID.fwPreflight = fwPreflight;
    cacheUpdateACID.fwSource = fwSource;
    cacheUpdateACID.fACID2 = fACID2;
    cacheUpdateACID.fACID3 = fACID3;
    cacheUpdateACID.fACID4 = fACID4;
    cacheUpdateACID.fACID5 = fACID5;
    cacheUpdateACID.fACID6 = fACID6;
    cacheUpdateACID.fTemp2 = fTemp2;
    return cacheUpdateACID;
}

export interface UpdateStatus {
    cMsgID: number;
    wRecordID: number;
    fStatus: number;
    generatedName_5: number;
}

// @ts-ignore
let cacheUpdateStatus: UpdateStatus = {};

export function parseUpdateStatus(buffer: ArrayBuffer): UpdateStatus {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wRecordID = view.getUint16(1, true);

    // Decoding bits 3,5
    const tmp3 = view.getUint8(3);
    const fStatus = tmp3 & 0x7;
    const generatedName_5 = (tmp3 & 0xf8) >>> 3;
    // Total size: 4 bytes
    cacheUpdateStatus.cMsgID = cMsgID;
    cacheUpdateStatus.wRecordID = wRecordID;
    cacheUpdateStatus.fStatus = fStatus;
    cacheUpdateStatus.generatedName_5 = generatedName_5;
    return cacheUpdateStatus;
}

export interface UpdateStatus2 {
    cMsgID: number;
    wRecordIDlsb: number;
    fwStatus: number;
    fwRecordIDmsb: number;
    fwT1: number;
}

// @ts-ignore
let cacheUpdateStatus2: UpdateStatus2 = {};

export function parseUpdateStatus2(buffer: ArrayBuffer): UpdateStatus2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wRecordIDlsb = view.getUint16(1, true);

    // Decoding bits 4,2,2
    const tmp3 = view.getUint8(3);
    const fwStatus = tmp3 & 0xf;
    const fwRecordIDmsb = (tmp3 & 0x30) >>> 4;
    const fwT1 = (tmp3 & 0xc0) >>> 6;
    // Total size: 4 bytes
    cacheUpdateStatus2.cMsgID = cMsgID;
    cacheUpdateStatus2.wRecordIDlsb = wRecordIDlsb;
    cacheUpdateStatus2.fwStatus = fwStatus;
    cacheUpdateStatus2.fwRecordIDmsb = fwRecordIDmsb;
    cacheUpdateStatus2.fwT1 = fwT1;
    return cacheUpdateStatus2;
}

export interface Update3PDIHi {
    cMsgID: number;
    cLength: number;
    fwRecordID: number;
    fwFuelOnBoardUnit: number;
    fwPreflight: number;
    fwT1: number;
    fwFuelOnBoard: number;
    fwT2: number;
    fwDerivedPlannedFuelUnit: number;
    fwDerivedPlannedAltUnit: number;
    wDerivedPlannedAlt: number;
    dwDerivedPlannedFuelOnBoard: number;
    dwFuelOnBoardFEWP: number;
}

// @ts-ignore
let cacheUpdate3PDIHi: Update3PDIHi = {};

export function parseUpdate3PDIHi(buffer: ArrayBuffer): Update3PDIHi {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const cLength = view.getUint8(1);

    // Decoding bits 18,1,1,12
    const tmp2 = view.getUint32(2, true);
    const fwRecordID = tmp2 & 0x3ffff;
    const fwFuelOnBoardUnit = (tmp2 & 0x40000) >>> 18;
    const fwPreflight = (tmp2 & 0x80000) >>> 19;
    const fwT1 = (tmp2 & 0xfff00000) >>> 20;

    // Decoding bits 20,8,3,1
    const tmp6 = view.getUint32(6, true);
    const fwFuelOnBoard = tmp6 & 0xfffff;
    const fwT2 = (tmp6 & 0xff00000) >>> 20;
    const fwDerivedPlannedFuelUnit = (tmp6 & 0x70000000) >>> 28;
    const fwDerivedPlannedAltUnit = (tmp6 & 0x80000000) >>> 31;
    const wDerivedPlannedAlt = view.getUint16(10, true);
    // Check view byte length, older replay files have shorter version of the message
    const dwDerivedPlannedFuelOnBoard = view.byteLength >= 16 ? view.getUint32(12, true) : 0xffffffff;
    const dwFuelOnBoardFEWP = view.byteLength >= 20 ? view.getUint32(16, true) : 0xffffffff;

    // Total size: 16 bytes
    cacheUpdate3PDIHi.cMsgID = cMsgID;
    cacheUpdate3PDIHi.cLength = cLength;
    cacheUpdate3PDIHi.fwRecordID = fwRecordID;
    cacheUpdate3PDIHi.fwFuelOnBoardUnit = fwFuelOnBoardUnit;
    cacheUpdate3PDIHi.fwPreflight = fwPreflight;
    cacheUpdate3PDIHi.fwT1 = fwT1;
    cacheUpdate3PDIHi.fwFuelOnBoard = fwFuelOnBoard;
    cacheUpdate3PDIHi.fwT2 = fwT2;
    cacheUpdate3PDIHi.fwDerivedPlannedFuelUnit = fwDerivedPlannedFuelUnit;
    cacheUpdate3PDIHi.fwDerivedPlannedAltUnit = fwDerivedPlannedAltUnit;
    cacheUpdate3PDIHi.wDerivedPlannedAlt = wDerivedPlannedAlt;
    cacheUpdate3PDIHi.dwDerivedPlannedFuelOnBoard = dwDerivedPlannedFuelOnBoard;
    cacheUpdate3PDIHi.dwFuelOnBoardFEWP = dwFuelOnBoardFEWP;
    return cacheUpdate3PDIHi;
}

export interface Update3PDIMed {
    cMsgID: number;
    cLength: number;
    fwRecordID: number;
    fwPriority: number;
    fwEDCTHour: number;
    fwEDCTMin: number;
    fwDelayMinutes: number;
    fwEDCTDay: number;
    fwPreflight: number;
    fwT1: number;
    pcControlElem: string;
    fwCTAHour: number;
    fwCTAMin: number;
    fwCTADay: number;
    fwInitialDelRsn: number;
    fwLastDelRsn: number;
    fwT2: number;
}

// @ts-ignore
let cacheUpdate3PDIMed: Update3PDIMed = {};

export function parseUpdate3PDIMed(buffer: ArrayBuffer): Update3PDIMed {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const cLength = view.getUint8(1);

    // Decoding bits 18,3,5,6
    const tmp2 = view.getUint32(2, true);
    const fwRecordID = tmp2 & 0x3ffff;
    const fwPriority = (tmp2 & 0x1c0000) >>> 18;
    const fwEDCTHour = (tmp2 & 0x3e00000) >>> 21;
    const fwEDCTMin = (tmp2 & 0xfc000000) >>> 26;

    // Decoding bits 9,3,1,3
    const tmp6 = view.getUint16(6, true);
    const fwDelayMinutes = tmp6 & 0x1ff;
    const fwEDCTDay = (tmp6 & 0xe00) >>> 9;
    const fwPreflight = (tmp6 & 0x1000) >>> 12;
    const fwT1 = (tmp6 & 0xe000) >>> 13;
    const chars_8 = [];
    for (let i = 0; i < 10; i++) {
        const char = view.getUint8(8 + i);
        if (char === 0) break;
        chars_8.push(String.fromCharCode(char));
    }
    const pcControlElem = chars_8.join('').trim();

    // Decoding bits 5,6,3,2,5,11
    const tmp18 = view.getUint32(18, true);
    const fwCTAHour = tmp18 & 0x1f;
    const fwCTAMin = (tmp18 & 0x7e0) >>> 5;
    const fwCTADay = (tmp18 & 0x3800) >>> 11;
    const fwInitialDelRsn = (tmp18 & 0xc000) >>> 14;
    const fwLastDelRsn = (tmp18 & 0x1f0000) >>> 16;
    const fwT2 = (tmp18 & 0xffe00000) >>> 21;
    // Total size: 22 bytes
    cacheUpdate3PDIMed.cMsgID = cMsgID;
    cacheUpdate3PDIMed.cLength = cLength;
    cacheUpdate3PDIMed.fwRecordID = fwRecordID;
    cacheUpdate3PDIMed.fwPriority = fwPriority;
    cacheUpdate3PDIMed.fwEDCTHour = fwEDCTHour;
    cacheUpdate3PDIMed.fwEDCTMin = fwEDCTMin;
    cacheUpdate3PDIMed.fwDelayMinutes = fwDelayMinutes;
    cacheUpdate3PDIMed.fwEDCTDay = fwEDCTDay;
    cacheUpdate3PDIMed.fwPreflight = fwPreflight;
    cacheUpdate3PDIMed.fwT1 = fwT1;
    cacheUpdate3PDIMed.pcControlElem = pcControlElem;
    cacheUpdate3PDIMed.fwCTAHour = fwCTAHour;
    cacheUpdate3PDIMed.fwCTAMin = fwCTAMin;
    cacheUpdate3PDIMed.fwCTADay = fwCTADay;
    cacheUpdate3PDIMed.fwInitialDelRsn = fwInitialDelRsn;
    cacheUpdate3PDIMed.fwLastDelRsn = fwLastDelRsn;
    cacheUpdate3PDIMed.fwT2 = fwT2;
    return cacheUpdate3PDIMed;
}

export interface AltAirportRecord {
    id: string;
    type: AlternateDestinationRecordType;
}

export interface Update3PDILo {
    cMsgID: number;
    cLength: number;
    fwRecordID: number;
    fwNumSeats: number;
    fwIrrOpType: number;
    fwT1: number;
    fwNumPassengers: number;
    fwNumCrew: number;
    fwPreflight: number;
    pcCrewID: string;
    pcScratchpad: string;
    pcOriginGate: string;
    pcDestGate: string;
    pcRegNum: string;
    pcNoseNum: string;
    pcAlternateAirport: string;
    pcDepartureCenter: string;
    pcArrivalCenter: string;
    fwFuelReserve: number;
    fwFuelReserveUnit: number;
    fwOUTHour: number;
    fwOUTMinute: number;
    pcUserField1: string;
    fwOUTJDay: number;
    fwONMinute: number;
    fwINMinute: number;
    fwT2: number;
    fwINHour: number;
    fwONHour: number;
    fwONJDay: number;
    fwINJDay: number;
    pcUserField2: string;
    fwOFFJDay: number;
    fwOFFHour: number;
    fwOFFMinute: number;
    fwBlockINJDay: number;
    fwBlockINHour: number;
    fwBlockINMinute: number;
    fwDivRecPriority: number;
    pcAlternateAirport2: string;
    psAltApts?: AltAirportRecord[];
}

// @ts-ignore
let cacheUpdate3PDILo: Update3PDILo = {};

export function parseUpdate3PDILo(buffer: ArrayBuffer): Update3PDILo {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const cLength = view.getUint8(1);

    // Decoding bits 18,10,3,1
    const tmp2 = view.getUint32(2, true);
    const fwRecordID = tmp2 & 0x3ffff;
    const fwNumSeats = (tmp2 & 0xffc0000) >>> 18;
    const fwIrrOpType = (tmp2 & 0x70000000) >>> 28;
    const fwT1 = (tmp2 & 0x80000000) >>> 31;

    // Decoding bits 10,5,1
    const tmp6 = view.getUint16(6, true);
    const fwNumPassengers = tmp6 & 0x3ff;
    const fwNumCrew = (tmp6 & 0x7c00) >>> 10;
    const fwPreflight = (tmp6 & 0x8000) >>> 15;
    const chars_8 = [];
    for (let i = 0; i < 9; i++) {
        const char = view.getUint8(8 + i);
        if (char === 0) break;
        chars_8.push(String.fromCharCode(char));
    }
    const pcCrewID = chars_8.join(''); // Don't trim this. If this file is re-generated restore this line
    const chars_17 = [];
    for (let i = 0; i < 15; i++) {
        const char = view.getUint8(17 + i);
        if (char === 0) break;
        chars_17.push(String.fromCharCode(char));
    }
    const pcScratchpad = chars_17.join('').trim();
    const chars_32 = [];
    for (let i = 0; i < 5; i++) {
        const char = view.getUint8(32 + i);
        if (char === 0) break;
        chars_32.push(String.fromCharCode(char));
    }
    const pcOriginGate = chars_32.join('').trim();
    const chars_37 = [];
    for (let i = 0; i < 5; i++) {
        const char = view.getUint8(37 + i);
        if (char === 0) break;
        chars_37.push(String.fromCharCode(char));
    }
    const pcDestGate = chars_37.join('').trim();
    const chars_42 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(42 + i);
        if (char === 0) break;
        chars_42.push(String.fromCharCode(char));
    }
    const pcRegNum = chars_42.join('').trim();
    const chars_49 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(49 + i);
        if (char === 0) break;
        chars_49.push(String.fromCharCode(char));
    }
    const pcNoseNum = chars_49.join('').trim();
    const chars_56 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(56 + i);
        if (char === 0) break;
        chars_56.push(String.fromCharCode(char));
    }
    const pcAlternateAirport = chars_56.join('').trim();
    const chars_60 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(60 + i);
        if (char === 0) break;
        chars_60.push(String.fromCharCode(char));
    }
    const pcDepartureCenter = chars_60.join('').trim();
    const chars_64 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(64 + i);
        if (char === 0) break;
        chars_64.push(String.fromCharCode(char));
    }
    const pcArrivalCenter = chars_64.join('').trim();

    // Decoding bits 20,1,5,6
    const tmp68 = view.getUint32(68, true);
    const fwFuelReserve = tmp68 & 0xfffff;
    const fwFuelReserveUnit = (tmp68 & 0x100000) >>> 20;
    const fwOUTHour = (tmp68 & 0x3e00000) >>> 21;
    const fwOUTMinute = (tmp68 & 0xfc000000) >>> 26;
    const chars_72 = [];
    for (let i = 0; i < 5; i++) {
        const char = view.getUint8(72 + i);
        if (char === 0) break;
        chars_72.push(String.fromCharCode(char));
    }
    const pcUserField1 = chars_72.join('').trim();

    // Decoding bits 3,6,6,1
    const tmp77 = view.getUint16(77, true);
    const fwOUTJDay = tmp77 & 0x7;
    const fwONMinute = (tmp77 & 0x1f8) >>> 3;
    const fwINMinute = (tmp77 & 0x7e00) >>> 9;
    const fwT2 = (tmp77 & 0x8000) >>> 15;

    // Decoding bits 5,5,3,3
    const tmp79 = view.getUint16(79, true);
    const fwINHour = tmp79 & 0x1f;
    const fwONHour = (tmp79 & 0x3e0) >>> 5;
    const fwONJDay = (tmp79 & 0x1c00) >>> 10;
    const fwINJDay = (tmp79 & 0xe000) >>> 13;
    const chars_81 = [];
    for (let i = 0; i < 5; i++) {
        const char = view.getUint8(81 + i);
        if (char === 0) break;
        chars_81.push(String.fromCharCode(char));
    }
    const pcUserField2 = chars_81.join('').trim();

    // Decoding bits 3,5,6,3,5,6,4
    const tmp86 = view.getUint32(86, true);
    const fwOFFJDay = tmp86 & 0x7;
    const fwOFFHour = (tmp86 & 0xf8) >>> 3;
    const fwOFFMinute = (tmp86 & 0x3f00) >>> 8;
    const fwBlockINJDay = (tmp86 & 0x1c000) >>> 14;
    const fwBlockINHour = (tmp86 & 0x3e0000) >>> 17;
    const fwBlockINMinute = (tmp86 & 0xfc00000) >>> 22;
    const fwDivRecPriority = (tmp86 & 0xf0000000) >>> 28;
    const chars_90 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(90 + i);
        if (char === 0) break;
        chars_90.push(String.fromCharCode(char));
    }
    const pcAlternateAirport2 = chars_90.join('').trim();
    let psAltAirports: AltAirportRecord[] | undefined = undefined;

    if (cLength > 94) {
        psAltAirports = [];
        // since build 433
        assert(cLength >= 94 + 3 * 10);
        for (let i = 0; i < 10; i++) {
            const temp87 = view.getUint16(94 + i * 3, true);
            const temp88 = view.getUint8(94 + i * 3 + 2);
            // Check if first char is a "space", if it is skip
            if ((temp87 & 0x1f) === 31) {
                continue;
            }

            const type = (temp88 >> 5) & 0x07;
            if (type === 0) {
                break;
            }
            // YES, those are 5 LSB of char code... just add 65
            const id = [(temp87 >> 0) & 0x1f, (temp87 >> 5) & 0x1f, (temp87 >> 10) & 0x1f, temp88 & 0x1f]
                .map((x) => String.fromCharCode(x + 65))
                .join('');

            psAltAirports.push({
                id: id,
                type: type
            });
        }
    }

    // Total size: 94 bytes
    cacheUpdate3PDILo.cMsgID = cMsgID;
    cacheUpdate3PDILo.cLength = cLength;
    cacheUpdate3PDILo.fwRecordID = fwRecordID;
    cacheUpdate3PDILo.fwNumSeats = fwNumSeats;
    cacheUpdate3PDILo.fwIrrOpType = fwIrrOpType;
    cacheUpdate3PDILo.fwT1 = fwT1;
    cacheUpdate3PDILo.fwNumPassengers = fwNumPassengers;
    cacheUpdate3PDILo.fwNumCrew = fwNumCrew;
    cacheUpdate3PDILo.fwPreflight = fwPreflight;
    cacheUpdate3PDILo.pcCrewID = pcCrewID;
    cacheUpdate3PDILo.pcScratchpad = pcScratchpad;
    cacheUpdate3PDILo.pcOriginGate = pcOriginGate;
    cacheUpdate3PDILo.pcDestGate = pcDestGate;
    cacheUpdate3PDILo.pcRegNum = pcRegNum;
    cacheUpdate3PDILo.pcNoseNum = pcNoseNum;
    cacheUpdate3PDILo.pcAlternateAirport = pcAlternateAirport;
    cacheUpdate3PDILo.pcDepartureCenter = pcDepartureCenter;
    cacheUpdate3PDILo.pcArrivalCenter = pcArrivalCenter;
    cacheUpdate3PDILo.fwFuelReserve = fwFuelReserve;
    cacheUpdate3PDILo.fwFuelReserveUnit = fwFuelReserveUnit;
    cacheUpdate3PDILo.fwOUTHour = fwOUTHour;
    cacheUpdate3PDILo.fwOUTMinute = fwOUTMinute;
    cacheUpdate3PDILo.pcUserField1 = pcUserField1;
    cacheUpdate3PDILo.fwOUTJDay = fwOUTJDay;
    cacheUpdate3PDILo.fwONMinute = fwONMinute;
    cacheUpdate3PDILo.fwINMinute = fwINMinute;
    cacheUpdate3PDILo.fwT2 = fwT2;
    cacheUpdate3PDILo.fwINHour = fwINHour;
    cacheUpdate3PDILo.fwONHour = fwONHour;
    cacheUpdate3PDILo.fwONJDay = fwONJDay;
    cacheUpdate3PDILo.fwINJDay = fwINJDay;
    cacheUpdate3PDILo.pcUserField2 = pcUserField2;
    cacheUpdate3PDILo.fwOFFJDay = fwOFFJDay;
    cacheUpdate3PDILo.fwOFFHour = fwOFFHour;
    cacheUpdate3PDILo.fwOFFMinute = fwOFFMinute;
    cacheUpdate3PDILo.fwBlockINJDay = fwBlockINJDay;
    cacheUpdate3PDILo.fwBlockINHour = fwBlockINHour;
    cacheUpdate3PDILo.fwBlockINMinute = fwBlockINMinute;
    cacheUpdate3PDILo.fwDivRecPriority = fwDivRecPriority;
    cacheUpdate3PDILo.pcAlternateAirport2 = pcAlternateAirport2;
    cacheUpdate3PDILo.psAltApts = psAltAirports;
    return cacheUpdate3PDILo;
}

export interface UpdatePropNA2 {
    cMsgID: number;
    wSrvRec: number;
    fACID5: number;
    fArrMin: number;
    fFPSeq: number;
    fACID0: number;
    fACID1: number;
    fACID2: number;
    fACID3: number;
    fACID4: number;
    fT2: number;
    fACID6: number;
    fDepHour: number;
    fArrHour: number;
    fSpeed: number;
    fACType0: number;
    fACType1: number;
    fACType2: number;
    fDepDay: number;
    fT3: number;
    fACType3: number;
    fAltitude: number;
    fBlocked: number;
    fStatus: number;
    fArrDayMSB: number;
    fOrigin0: number;
    fOrigin1: number;
    fOrigin2: number;
    fOrigin3: number;
    fDepMin: number;
    fArrDayLSB: number;
    fDest0: number;
    fDest1: number;
    fDest2: number;
    fDest3: number;
    fArrMinLSB4: number;
    fOETDHour: number;
    fOETDMin: number;
    fOETDDay: number;
    fOETAHour: number;
    fOETAMin: number;
    fOETADay: number;
    fSpeedType: number;
}

// @ts-ignore
let cacheUpdatePropNA2: UpdatePropNA2 = {};

export function parseUpdatePropNA2(buffer: ArrayBuffer): UpdatePropNA2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,6,6,2
    const tmp1 = view.getUint32(1, true);
    const wSrvRec = tmp1 & 0x3ffff;
    const fACID5 = (tmp1 & 0xfc0000) >>> 18;
    const fArrMin = (tmp1 & 0x3f000000) >>> 24;
    const fFPSeq = (tmp1 & 0xc0000000) >>> 30;

    // Decoding bits 6,6,6,6,6,2
    const tmp5 = view.getUint32(5, true);
    const fACID0 = tmp5 & 0x3f;
    const fACID1 = (tmp5 & 0xfc0) >>> 6;
    const fACID2 = (tmp5 & 0x3f000) >>> 12;
    const fACID3 = (tmp5 & 0xfc0000) >>> 18;
    const fACID4 = (tmp5 & 0x3f000000) >>> 24;
    const fT2 = (tmp5 & 0xc0000000) >>> 30;

    // Decoding bits 6,5,5,10,6
    const tmp9 = view.getUint32(9, true);
    const fACID6 = tmp9 & 0x3f;
    const fDepHour = (tmp9 & 0x7c0) >>> 6;
    const fArrHour = (tmp9 & 0xf800) >>> 11;
    const fSpeed = (tmp9 & 0x3ff0000) >>> 16;
    const fACType0 = (tmp9 & 0xfc000000) >>> 26;

    // Decoding bits 6,6,3,1,6,9,1
    const tmp13 = view.getUint32(13, true);
    const fACType1 = tmp13 & 0x3f;
    const fACType2 = (tmp13 & 0xfc0) >>> 6;
    const fDepDay = (tmp13 & 0x7000) >>> 12;
    const fT3 = (tmp13 & 0x8000) >>> 15;
    const fACType3 = (tmp13 & 0x3f0000) >>> 16;
    const fAltitude = (tmp13 & 0x7fc00000) >>> 22;
    const fBlocked = (tmp13 & 0x80000000) >>> 31;

    // Decoding bits 3,1,6,6
    const tmp17 = view.getUint16(17, true);
    const fStatus = tmp17 & 0x7;
    const fArrDayMSB = (tmp17 & 0x8) >>> 3;
    const fOrigin0 = (tmp17 & 0x3f0) >>> 4;
    const fOrigin1 = (tmp17 & 0xfc00) >>> 10;

    // Decoding bits 6,6,6,2,6,6
    const tmp19 = view.getUint32(19, true);
    const fOrigin2 = tmp19 & 0x3f;
    const fOrigin3 = (tmp19 & 0xfc0) >>> 6;
    const fDepMin = (tmp19 & 0x3f000) >>> 12;
    const fArrDayLSB = (tmp19 & 0xc0000) >>> 18;
    const fDest0 = (tmp19 & 0x3f00000) >>> 20;
    const fDest1 = (tmp19 & 0xfc000000) >>> 26;

    // Decoding bits 6,6,4
    const tmp23 = view.getUint16(23, true);
    const fDest2 = tmp23 & 0x3f;
    const fDest3 = (tmp23 & 0xfc0) >>> 6;
    const fArrMinLSB4 = (tmp23 & 0xf000) >>> 12;

    // Decoding bits 5,6,3,5,6,3,4
    const tmp25 = view.getUint32(25, true);
    const fOETDHour = tmp25 & 0x1f;
    const fOETDMin = (tmp25 & 0x7e0) >>> 5;
    const fOETDDay = (tmp25 & 0x3800) >>> 11;
    const fOETAHour = (tmp25 & 0x7c000) >>> 14;
    const fOETAMin = (tmp25 & 0x1f80000) >>> 19;
    const fOETADay = (tmp25 & 0xe000000) >>> 25;
    const fSpeedType = (tmp25 & 0xf0000000) >>> 28;
    // Total size: 29 bytes
    cacheUpdatePropNA2.cMsgID = cMsgID;
    cacheUpdatePropNA2.wSrvRec = wSrvRec;
    cacheUpdatePropNA2.fACID5 = fACID5;
    cacheUpdatePropNA2.fArrMin = fArrMin;
    cacheUpdatePropNA2.fFPSeq = fFPSeq;
    cacheUpdatePropNA2.fACID0 = fACID0;
    cacheUpdatePropNA2.fACID1 = fACID1;
    cacheUpdatePropNA2.fACID2 = fACID2;
    cacheUpdatePropNA2.fACID3 = fACID3;
    cacheUpdatePropNA2.fACID4 = fACID4;
    cacheUpdatePropNA2.fT2 = fT2;
    cacheUpdatePropNA2.fACID6 = fACID6;
    cacheUpdatePropNA2.fDepHour = fDepHour;
    cacheUpdatePropNA2.fArrHour = fArrHour;
    cacheUpdatePropNA2.fSpeed = fSpeed;
    cacheUpdatePropNA2.fACType0 = fACType0;
    cacheUpdatePropNA2.fACType1 = fACType1;
    cacheUpdatePropNA2.fACType2 = fACType2;
    cacheUpdatePropNA2.fDepDay = fDepDay;
    cacheUpdatePropNA2.fT3 = fT3;
    cacheUpdatePropNA2.fACType3 = fACType3;
    cacheUpdatePropNA2.fAltitude = fAltitude;
    cacheUpdatePropNA2.fBlocked = fBlocked;
    cacheUpdatePropNA2.fStatus = fStatus;
    cacheUpdatePropNA2.fArrDayMSB = fArrDayMSB;
    cacheUpdatePropNA2.fOrigin0 = fOrigin0;
    cacheUpdatePropNA2.fOrigin1 = fOrigin1;
    cacheUpdatePropNA2.fOrigin2 = fOrigin2;
    cacheUpdatePropNA2.fOrigin3 = fOrigin3;
    cacheUpdatePropNA2.fDepMin = fDepMin;
    cacheUpdatePropNA2.fArrDayLSB = fArrDayLSB;
    cacheUpdatePropNA2.fDest0 = fDest0;
    cacheUpdatePropNA2.fDest1 = fDest1;
    cacheUpdatePropNA2.fDest2 = fDest2;
    cacheUpdatePropNA2.fDest3 = fDest3;
    cacheUpdatePropNA2.fArrMinLSB4 = fArrMinLSB4;
    cacheUpdatePropNA2.fOETDHour = fOETDHour;
    cacheUpdatePropNA2.fOETDMin = fOETDMin;
    cacheUpdatePropNA2.fOETDDay = fOETDDay;
    cacheUpdatePropNA2.fOETAHour = fOETAHour;
    cacheUpdatePropNA2.fOETAMin = fOETAMin;
    cacheUpdatePropNA2.fOETADay = fOETADay;
    cacheUpdatePropNA2.fSpeedType = fSpeedType;
    return cacheUpdatePropNA2;
}

export interface UpdatePropA2 {
    cMsgID: number;
    wSrvRec: number;
    fArrMin: number;
    fStatus: number;
    fDepDay: number;
    fArrDayRel: number;
    wPtRec: number;
    fDepHour: number;
    fDepMin: number;
    fArrHour: number;
    fOETDHour: number;
    fOETDMin: number;
    fOETDDay: number;
    fOETAHour: number;
    fOETAMin: number;
    fOETADay: number;
    fT1: number;
}

// @ts-ignore
let cacheUpdatePropA2: UpdatePropA2 = {};

export function parseUpdatePropA2(buffer: ArrayBuffer): UpdatePropA2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 18,6,3,3,2
    const tmp1 = view.getUint32(1, true);
    const wSrvRec = tmp1 & 0x3ffff;
    const fArrMin = (tmp1 & 0xfc0000) >>> 18;
    const fStatus = (tmp1 & 0x7000000) >>> 24;
    const fDepDay = (tmp1 & 0x38000000) >>> 27;
    const fArrDayRel = (tmp1 & 0xc0000000) >>> 30;
    const wPtRec = view.getUint16(5, true);

    // Decoding bits 5,6,5
    const tmp7 = view.getUint16(7, true);
    const fDepHour = tmp7 & 0x1f;
    const fDepMin = (tmp7 & 0x7e0) >>> 5;
    const fArrHour = (tmp7 & 0xf800) >>> 11;

    // Decoding bits 5,6,3,5,6,3,4
    const tmp9 = view.getUint32(9, true);
    const fOETDHour = tmp9 & 0x1f;
    const fOETDMin = (tmp9 & 0x7e0) >>> 5;
    const fOETDDay = (tmp9 & 0x3800) >>> 11;
    const fOETAHour = (tmp9 & 0x7c000) >>> 14;
    const fOETAMin = (tmp9 & 0x1f80000) >>> 19;
    const fOETADay = (tmp9 & 0xe000000) >>> 25;
    const fT1 = (tmp9 & 0xf0000000) >>> 28;
    // Total size: 13 bytes
    cacheUpdatePropA2.cMsgID = cMsgID;
    cacheUpdatePropA2.wSrvRec = wSrvRec;
    cacheUpdatePropA2.fArrMin = fArrMin;
    cacheUpdatePropA2.fStatus = fStatus;
    cacheUpdatePropA2.fDepDay = fDepDay;
    cacheUpdatePropA2.fArrDayRel = fArrDayRel;
    cacheUpdatePropA2.wPtRec = wPtRec;
    cacheUpdatePropA2.fDepHour = fDepHour;
    cacheUpdatePropA2.fDepMin = fDepMin;
    cacheUpdatePropA2.fArrHour = fArrHour;
    cacheUpdatePropA2.fOETDHour = fOETDHour;
    cacheUpdatePropA2.fOETDMin = fOETDMin;
    cacheUpdatePropA2.fOETDDay = fOETDDay;
    cacheUpdatePropA2.fOETAHour = fOETAHour;
    cacheUpdatePropA2.fOETAMin = fOETAMin;
    cacheUpdatePropA2.fOETADay = fOETADay;
    cacheUpdatePropA2.fT1 = fT1;
    return cacheUpdatePropA2;
}

export interface MFLKLResp {
    cMsgID: number;
    fMFListRecNo: number;
    fDest0: number;
    fDest1: number;
    fDest2: number;
    fDest3: number;
    fOrigin0: number;
    fOrigin1: number;
    fOrigin2: number;
    fOrigin3: number;
    fStatus: number;
    fYear: number;
    fMonth: number;
    fDay: number;
    fHour: number;
    fMinute: number;
    fACType0: number;
    fACType1: number;
    fACType2: number;
    generatedName_9: number;
    fACType3: number;
    generatedName_10: number;
    iLatitude: number;
    iLongitude: number;
}

// @ts-ignore
let cacheMFLKLResp: MFLKLResp = {};

export function parseMFLKLResp(buffer: ArrayBuffer): MFLKLResp {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);

    // Decoding bits 14,6,6,6
    const tmp1 = view.getUint32(1, true);
    const fMFListRecNo = tmp1 & 0x3fff;
    const fDest0 = (tmp1 & 0xfc000) >>> 14;
    const fDest1 = (tmp1 & 0x3f00000) >>> 20;
    const fDest2 = (tmp1 & 0xfc000000) >>> 26;

    // Decoding bits 6,6,6,6,6,2
    const tmp5 = view.getUint32(5, true);
    const fDest3 = tmp5 & 0x3f;
    const fOrigin0 = (tmp5 & 0xfc0) >>> 6;
    const fOrigin1 = (tmp5 & 0x3f000) >>> 12;
    const fOrigin2 = (tmp5 & 0xfc0000) >>> 18;
    const fOrigin3 = (tmp5 & 0x3f000000) >>> 24;
    const fStatus = (tmp5 & 0xc0000000) >>> 30;

    // Decoding bits 6,4,5,5,6,6
    const tmp9 = view.getUint32(9, true);
    const fYear = tmp9 & 0x3f;
    const fMonth = (tmp9 & 0x3c0) >>> 6;
    const fDay = (tmp9 & 0x7c00) >>> 10;
    const fHour = (tmp9 & 0xf8000) >>> 15;
    const fMinute = (tmp9 & 0x3f00000) >>> 20;
    const fACType0 = (tmp9 & 0xfc000000) >>> 26;

    // Decoding bits 6,6,4
    const tmp13 = view.getUint16(13, true);
    const fACType1 = tmp13 & 0x3f;
    const fACType2 = (tmp13 & 0xfc0) >>> 6;
    const generatedName_9 = (tmp13 & 0xf000) >>> 12;

    // Decoding bits 6,2
    const tmp15 = view.getUint8(15);
    const fACType3 = tmp15 & 0x3f;
    const generatedName_10 = (tmp15 & 0xc0) >>> 6;
    const iLatitude = view.getInt32(16, true);
    const iLongitude = view.getInt32(20, true);
    // Total size: 24 bytes
    cacheMFLKLResp.cMsgID = cMsgID;
    cacheMFLKLResp.fMFListRecNo = fMFListRecNo;
    cacheMFLKLResp.fDest0 = fDest0;
    cacheMFLKLResp.fDest1 = fDest1;
    cacheMFLKLResp.fDest2 = fDest2;
    cacheMFLKLResp.fDest3 = fDest3;
    cacheMFLKLResp.fOrigin0 = fOrigin0;
    cacheMFLKLResp.fOrigin1 = fOrigin1;
    cacheMFLKLResp.fOrigin2 = fOrigin2;
    cacheMFLKLResp.fOrigin3 = fOrigin3;
    cacheMFLKLResp.fStatus = fStatus;
    cacheMFLKLResp.fYear = fYear;
    cacheMFLKLResp.fMonth = fMonth;
    cacheMFLKLResp.fDay = fDay;
    cacheMFLKLResp.fHour = fHour;
    cacheMFLKLResp.fMinute = fMinute;
    cacheMFLKLResp.fACType0 = fACType0;
    cacheMFLKLResp.fACType1 = fACType1;
    cacheMFLKLResp.fACType2 = fACType2;
    cacheMFLKLResp.generatedName_9 = generatedName_9;
    cacheMFLKLResp.fACType3 = fACType3;
    cacheMFLKLResp.generatedName_10 = generatedName_10;
    cacheMFLKLResp.iLatitude = iLatitude;
    cacheMFLKLResp.iLongitude = iLongitude;
    return cacheMFLKLResp;
}

export interface Init1Start {
    cMsgID: number;
    wLength: number;
    wInit1Count: number;
    dwUpdateTo: number;
}

// @ts-ignore
let cacheInit1Start: Init1Start = {};

export function parseInit1Start(buffer: ArrayBuffer): Init1Start {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const wInit1Count = view.getUint16(3, true);
    const dwUpdateTo = view.getUint32(5, true);
    // Total size: 9 bytes
    cacheInit1Start.cMsgID = cMsgID;
    cacheInit1Start.wLength = wLength;
    cacheInit1Start.wInit1Count = wInit1Count;
    cacheInit1Start.dwUpdateTo = dwUpdateTo;
    return cacheInit1Start;
}

export interface Init1End {
    cMsgID: number;
    wLength: number;
    wActivePlaneCount: number;
    dwUpdateTo: number;
    wMsgSentCount: number;
    dwMsgSentChecksum: number;
}

// @ts-ignore
let cacheInit1End: Init1End = {};

export function parseInit1End(buffer: ArrayBuffer): Init1End {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const wActivePlaneCount = view.getUint16(3, true);
    const dwUpdateTo = view.getUint32(5, true);
    const wMsgSentCount = view.getUint16(9, true);
    const dwMsgSentChecksum = view.getUint32(11, true);
    // Total size: 15 bytes
    cacheInit1End.cMsgID = cMsgID;
    cacheInit1End.wLength = wLength;
    cacheInit1End.wActivePlaneCount = wActivePlaneCount;
    cacheInit1End.dwUpdateTo = dwUpdateTo;
    cacheInit1End.wMsgSentCount = wMsgSentCount;
    cacheInit1End.dwMsgSentChecksum = dwMsgSentChecksum;
    return cacheInit1End;
}

export interface Init2Start {
    cMsgID: number;
    wLength: number;
    wInit2Count: number;
    dwUpdateTo: number;
    iBaseJDay: number;
}

// @ts-ignore
let cacheInit2Start: Init2Start = {};

export function parseInit2Start(buffer: ArrayBuffer): Init2Start {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const wInit2Count = view.getUint16(3, true);
    const dwUpdateTo = view.getUint32(5, true);
    const iBaseJDay = view.getInt32(9, true);
    // Total size: 13 bytes
    cacheInit2Start.cMsgID = cMsgID;
    cacheInit2Start.wLength = wLength;
    cacheInit2Start.wInit2Count = wInit2Count;
    cacheInit2Start.dwUpdateTo = dwUpdateTo;
    cacheInit2Start.iBaseJDay = iBaseJDay;
    return cacheInit2Start;
}

export interface Init2End {
    cMsgID: number;
    wLength: number;
    wActivePlaneCount: number;
    dwUpdateTo: number;
    wMsgSentCount: number;
    dwMsgSentChecksum: number;
}

// @ts-ignore
let cacheInit2End: Init2End = {};

export function parseInit2End(buffer: ArrayBuffer): Init2End {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const wActivePlaneCount = view.getUint16(3, true);
    const dwUpdateTo = view.getUint32(5, true);
    const wMsgSentCount = view.getUint16(9, true);
    const dwMsgSentChecksum = view.getUint32(11, true);
    // Total size: 15 bytes
    cacheInit2End.cMsgID = cMsgID;
    cacheInit2End.wLength = wLength;
    cacheInit2End.wActivePlaneCount = wActivePlaneCount;
    cacheInit2End.dwUpdateTo = dwUpdateTo;
    cacheInit2End.wMsgSentCount = wMsgSentCount;
    cacheInit2End.dwMsgSentChecksum = dwMsgSentChecksum;
    return cacheInit2End;
}

export interface UpdateStart {
    cMsgID: number;
    wLength: number;
    dwUpdateTo: number;
    dwUpdateFrom: number;
    dwUpdateSeqNum: number;
    iBaseJDay: number;
}

// @ts-ignore
let cacheUpdateStart: UpdateStart = {};

export function parseUpdateStart(buffer: ArrayBuffer): UpdateStart {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const dwUpdateTo = view.getUint32(3, true);
    const dwUpdateFrom = view.getUint32(7, true);
    const dwUpdateSeqNum = view.getUint32(11, true);
    const iBaseJDay = view.getInt32(15, true);
    // Total size: 19 bytes
    cacheUpdateStart.cMsgID = cMsgID;
    cacheUpdateStart.wLength = wLength;
    cacheUpdateStart.dwUpdateTo = dwUpdateTo;
    cacheUpdateStart.dwUpdateFrom = dwUpdateFrom;
    cacheUpdateStart.dwUpdateSeqNum = dwUpdateSeqNum;
    cacheUpdateStart.iBaseJDay = iBaseJDay;
    return cacheUpdateStart;
}

export interface UpdateEnd {
    cMsgID: number;
    wLength: number;
    wActivePlaneCount: number;
    dwUpdateTo: number;
    dwUpdateFrom: number;
    dwUpdateSeqNum: number;
    wMsgSentCount: number;
    dwMsgSentChecksum: number;
}

// @ts-ignore
let cacheUpdateEnd: UpdateEnd = {};

export function parseUpdateEnd(buffer: ArrayBuffer): UpdateEnd {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const wActivePlaneCount = view.getUint16(3, true);
    const dwUpdateTo = view.getUint32(5, true);
    const dwUpdateFrom = view.getUint32(9, true);
    const dwUpdateSeqNum = view.getUint32(13, true);
    const wMsgSentCount = view.getUint16(17, true);
    const dwMsgSentChecksum = view.getUint32(19, true);
    // Total size: 23 bytes
    cacheUpdateEnd.cMsgID = cMsgID;
    cacheUpdateEnd.wLength = wLength;
    cacheUpdateEnd.wActivePlaneCount = wActivePlaneCount;
    cacheUpdateEnd.dwUpdateTo = dwUpdateTo;
    cacheUpdateEnd.dwUpdateFrom = dwUpdateFrom;
    cacheUpdateEnd.dwUpdateSeqNum = dwUpdateSeqNum;
    cacheUpdateEnd.wMsgSentCount = wMsgSentCount;
    cacheUpdateEnd.dwMsgSentChecksum = dwMsgSentChecksum;
    return cacheUpdateEnd;
}

export interface LoginResp {
    cMsgID: number;
    wLength: number;
    cResultCode: number;
    cServerMajorVersion: number;
    cServerMinorVersion: number;
    szServerID: string;
    wPlaneTableEntries: number;
    wPlaneCount: number;
    dwRights1: number;
    dwRights2: number;
    wClientIndex: number;
    cLatestClientMajor: number;
    cLatestClientMinor: number;
    wCurStaticDataVersion: number;
    dwRollOverIPAddress: number;
    cDaysTillExpire: number;
    pcExpDate: string;
    cClientType: number;
    dwFlags1: number;
    dwSessionID: number;
    cClientMode: number;
    cClientLevel: number;
    dwRights3: number;
    dwRights4: number;
    dwRights5: number;
    dwRights6: number;
    cFPMStatus: number;
    pcCrashDumpUserID: string;
    pcCrashDumpPassword: string;
    dwRights7: number;
    dwRights8: number;
    dwRights9: number;
    dwRights10: number;
    pczWebDNS1: string;
    pczWebDNS2: string;
}

// @ts-ignore
let cacheLoginResp: LoginResp = {};

export function parseLoginResp(buffer: ArrayBuffer): LoginResp {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const cResultCode = view.getUint8(3);
    const cServerMajorVersion = view.getUint8(4);
    const cServerMinorVersion = view.getUint8(5);
    const chars_6 = [];
    for (let i = 0; i < 11; i++) {
        const char = view.getUint8(6 + i);
        if (char === 0) break;
        chars_6.push(String.fromCharCode(char));
    }
    const szServerID = chars_6.join('').trim();
    const wPlaneTableEntries = view.getUint16(17, true);
    const wPlaneCount = view.getUint16(19, true);
    const dwRights1 = view.getUint32(21, true);
    const dwRights2 = view.getUint32(25, true);
    const wClientIndex = view.getUint16(29, true);
    const cLatestClientMajor = view.getUint8(31);
    const cLatestClientMinor = view.getUint8(32);
    const wCurStaticDataVersion = view.getUint16(33, true);
    const dwRollOverIPAddress = view.getUint32(35, true);
    const cDaysTillExpire = view.getUint8(39);
    const chars_40 = [];
    for (let i = 0; i < 8; i++) {
        const char = view.getUint8(40 + i);
        if (char === 0) break;
        chars_40.push(String.fromCharCode(char));
    }
    const pcExpDate = chars_40.join('').trim();
    const cClientType = view.getUint8(48);
    const dwFlags1 = view.getUint32(49, true);
    const dwSessionID = view.getUint32(53, true);
    const cClientMode = view.getUint8(57);
    const cClientLevel = view.getUint8(58);
    const dwRights3 = view.getUint32(59, true);
    const dwRights4 = view.getUint32(63, true);
    const dwRights5 = view.getUint32(67, true);
    const dwRights6 = view.getUint32(71, true);
    const cFPMStatus = view.getUint8(75);
    const chars_76 = [];
    for (let i = 0; i < 12; i++) {
        const char = view.getUint8(76 + i);
        if (char === 0) break;
        chars_76.push(String.fromCharCode(char));
    }
    const pcCrashDumpUserID = chars_76.join('').trim();
    const chars_88 = [];
    for (let i = 0; i < 12; i++) {
        const char = view.getUint8(88 + i);
        if (char === 0) break;
        chars_88.push(String.fromCharCode(char));
    }
    const pcCrashDumpPassword = chars_88.join('').trim();
    const dwRights7 = view.getUint32(100, true);
    const dwRights8 = view.getUint32(104, true);
    const dwRights9 = view.getUint32(108, true);
    const dwRights10 = view.getUint32(112, true);
    const chars_116 = [];
    for (let i = 0; i < 20; i++) {
        const char = view.getUint8(116 + i);
        if (char === 0) break;
        chars_116.push(String.fromCharCode(char));
    }
    const pczWebDNS1 = chars_116.join('').trim();
    const chars_136 = [];
    for (let i = 0; i < 20; i++) {
        const char = view.getUint8(136 + i);
        if (char === 0) break;
        chars_136.push(String.fromCharCode(char));
    }
    const pczWebDNS2 = chars_136.join('').trim();
    // Total size: 156 bytes
    cacheLoginResp.cMsgID = cMsgID;
    cacheLoginResp.wLength = wLength;
    cacheLoginResp.cResultCode = cResultCode;
    cacheLoginResp.cServerMajorVersion = cServerMajorVersion;
    cacheLoginResp.cServerMinorVersion = cServerMinorVersion;
    cacheLoginResp.szServerID = szServerID;
    cacheLoginResp.wPlaneTableEntries = wPlaneTableEntries;
    cacheLoginResp.wPlaneCount = wPlaneCount;
    cacheLoginResp.dwRights1 = dwRights1;
    cacheLoginResp.dwRights2 = dwRights2;
    cacheLoginResp.wClientIndex = wClientIndex;
    cacheLoginResp.cLatestClientMajor = cLatestClientMajor;
    cacheLoginResp.cLatestClientMinor = cLatestClientMinor;
    cacheLoginResp.wCurStaticDataVersion = wCurStaticDataVersion;
    cacheLoginResp.dwRollOverIPAddress = dwRollOverIPAddress;
    cacheLoginResp.cDaysTillExpire = cDaysTillExpire;
    cacheLoginResp.pcExpDate = pcExpDate;
    cacheLoginResp.cClientType = cClientType;
    cacheLoginResp.dwFlags1 = dwFlags1;
    cacheLoginResp.dwSessionID = dwSessionID;
    cacheLoginResp.cClientMode = cClientMode;
    cacheLoginResp.cClientLevel = cClientLevel;
    cacheLoginResp.dwRights3 = dwRights3;
    cacheLoginResp.dwRights4 = dwRights4;
    cacheLoginResp.dwRights5 = dwRights5;
    cacheLoginResp.dwRights6 = dwRights6;
    cacheLoginResp.cFPMStatus = cFPMStatus;
    cacheLoginResp.pcCrashDumpUserID = pcCrashDumpUserID;
    cacheLoginResp.pcCrashDumpPassword = pcCrashDumpPassword;
    cacheLoginResp.dwRights7 = dwRights7;
    cacheLoginResp.dwRights8 = dwRights8;
    cacheLoginResp.dwRights9 = dwRights9;
    cacheLoginResp.dwRights10 = dwRights10;
    cacheLoginResp.pczWebDNS1 = pczWebDNS1;
    cacheLoginResp.pczWebDNS2 = pczWebDNS2;
    return cacheLoginResp;
}

export interface TimeAdjust {
    cMsgID: number;
    wLength: number;
    sServerTime: SYSTEMTIME;
    dwServerTick: number;
}

// @ts-ignore
let cacheTimeAdjust: TimeAdjust = {};

export function parseTimeAdjust(buffer: ArrayBuffer): TimeAdjust {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const sServerTime = parseSYSTEMTIME(buffer.slice(3));
    const dwServerTick = view.getUint32(19, true);
    // Total size: 23 bytes
    cacheTimeAdjust.cMsgID = cMsgID;
    cacheTimeAdjust.wLength = wLength;
    cacheTimeAdjust.sServerTime = sServerTime;
    cacheTimeAdjust.dwServerTick = dwServerTick;
    return cacheTimeAdjust;
}

export interface ClientText {
    cMsgID: number;
    wLength: number;
    sServerTime: SYSTEMTIME;
    cImportance: number;
    wTextLength: number;
    pcText: string;
}

// @ts-ignore
let cacheClientText: ClientText = {};

export function parseClientText(buffer: ArrayBuffer): ClientText {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const sServerTime = parseSYSTEMTIME(buffer.slice(3));
    const cImportance = view.getUint8(19);
    const wTextLength = view.getUint16(20, true);
    let offset = 22;
    const chars_22 = [];
    for (let i = 0; i < wTextLength; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_22.push(String.fromCharCode(char));
    }
    offset += wTextLength;
    const pcText = chars_22.join('').trim();
    // Total size: 21 bytes
    cacheClientText.cMsgID = cMsgID;
    cacheClientText.wLength = wLength;
    cacheClientText.sServerTime = sServerTime;
    cacheClientText.cImportance = cImportance;
    cacheClientText.wTextLength = wTextLength;
    cacheClientText.pcText = pcText;
    return cacheClientText;
}

export interface WthrProdInfoV2 {
    cMsgID: number;
    wLength: number;
    dwIP1: number;
    dwIP2: number;
    pczDNS1: string;
    pczDNS2: string;
    pcUserID: Uint8Array;
    pcPassword: Uint8Array;
    cWeatherMode: number;
}

// @ts-ignore
let cacheWthrProdInfoV2: WthrProdInfoV2 = {};

export function parseWthrProdInfoV2(buffer: ArrayBuffer): WthrProdInfoV2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const dwIP1 = view.getUint32(3, true);
    const dwIP2 = view.getUint32(7, true);
    const chars_11 = [];
    for (let i = 0; i < 20; i++) {
        const char = view.getUint8(11 + i);
        if (char === 0) break;
        chars_11.push(String.fromCharCode(char));
    }
    const pczDNS1 = chars_11.join('').trim();
    const chars_31 = [];
    for (let i = 0; i < 20; i++) {
        const char = view.getUint8(31 + i);
        if (char === 0) break;
        chars_31.push(String.fromCharCode(char));
    }
    const pczDNS2 = chars_31.join('').trim();
    const pcUserID = new Uint8Array(buffer, 51, 20);
    const pcPassword = new Uint8Array(buffer, 71, 20);
    const cWeatherMode = view.getUint8(91);
    // Total size: 92 bytes
    cacheWthrProdInfoV2.cMsgID = cMsgID;
    cacheWthrProdInfoV2.wLength = wLength;
    cacheWthrProdInfoV2.dwIP1 = dwIP1;
    cacheWthrProdInfoV2.dwIP2 = dwIP2;
    cacheWthrProdInfoV2.pczDNS1 = pczDNS1;
    cacheWthrProdInfoV2.pczDNS2 = pczDNS2;
    cacheWthrProdInfoV2.pcUserID = pcUserID;
    cacheWthrProdInfoV2.pcPassword = pcPassword;
    cacheWthrProdInfoV2.cWeatherMode = cWeatherMode;
    return cacheWthrProdInfoV2;
}

export interface WthrProdStatusV2 {
    cMsgID: number;
    wLength: number;
    wProdCount: number;
    psProd: WthrProdStatusItemV2[];
}

// @ts-ignore
let cacheWthrProdStatusV2: WthrProdStatusV2 = {};

export function parseWthrProdStatusV2(buffer: ArrayBuffer): WthrProdStatusV2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const wProdCount = view.getUint16(3, true);
    const psProd = Array(wProdCount);
    for (let i = 0; i < wProdCount; i++) {
        psProd[i] = parseWthrProdStatusItemV2(buffer.slice(5 + i * 11));
    }
    // Total size: -6 bytes
    cacheWthrProdStatusV2.cMsgID = cMsgID;
    cacheWthrProdStatusV2.wLength = wLength;
    cacheWthrProdStatusV2.wProdCount = wProdCount;
    cacheWthrProdStatusV2.psProd = psProd;
    return cacheWthrProdStatusV2;
}

export interface StartupComplete {
    cMsgID: number;
    wLength: number;
}

// @ts-ignore
let cacheStartupComplete: StartupComplete = {};

export function parseStartupComplete(buffer: ArrayBuffer): StartupComplete {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    // Total size: 3 bytes
    cacheStartupComplete.cMsgID = cMsgID;
    cacheStartupComplete.wLength = wLength;
    return cacheStartupComplete;
}

export interface UpdateEvent {
    cMsgID: number;
    wLength: number;
    wRecID: number;
    cEventCount: number;
    psEvents: EVENT_REC[];
}

// @ts-ignore
let cacheUpdateEvent: UpdateEvent = {};

export function parseUpdateEvent(buffer: ArrayBuffer): UpdateEvent {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const wRecID = view.getUint16(3, true);
    const cEventCount = view.getUint8(5);
    const psEvents = Array(cEventCount);
    for (let i = 0; i < cEventCount; i++) {
        psEvents[i] = parseEVENT_REC(buffer.slice(6 + i * 8));
    }
    // Total size: -2 bytes
    cacheUpdateEvent.cMsgID = cMsgID;
    cacheUpdateEvent.wLength = wLength;
    cacheUpdateEvent.wRecID = wRecID;
    cacheUpdateEvent.cEventCount = cEventCount;
    cacheUpdateEvent.psEvents = psEvents;
    return cacheUpdateEvent;
}

export interface UpdateEvent2 {
    cMsgID: number;
    wLength: number;
    wRecordID: number;
    cEventCount: number;
    generatedName_12: number;
    psEvents: EVENT_REC2[];
}

// @ts-ignore
let cacheUpdateEvent2: UpdateEvent2 = {};

export function parseUpdateEvent2(buffer: ArrayBuffer): UpdateEvent2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);

    // Decoding bits 18,8,6
    const tmp3 = view.getUint32(3, true);
    const wRecordID = tmp3 & 0x3ffff;
    const cEventCount = (tmp3 & 0x3fc0000) >>> 18;
    const generatedName_12 = (tmp3 & 0xfc000000) >>> 26;

    let offset = 7;
    const psEvents: EVENT_REC2[] = [];

    // Parse EVENT_REC2 array
    for (let i = 0; i < cEventCount; i++) {
        const eventBuffer = buffer.slice(offset, offset + 20);
        const event = parseEVENT_REC2(eventBuffer);
        psEvents.push(event);
        offset += 20;
    }

    const eventsWithDetails = psEvents.filter((event) => event.bDetails === 1);
    for (const event of eventsWithDetails) {
        const chars = [];
        for (let i = 0; i < 30; i++) {
            const char = view.getUint8(offset + i);
            if (char === 0) break;
            chars.push(String.fromCharCode(char));
        }
        event.details = chars.join('').trim();
        offset += 30;
    }
    // Total size: -13 bytes
    cacheUpdateEvent2.cMsgID = cMsgID;
    cacheUpdateEvent2.wLength = wLength;
    cacheUpdateEvent2.wRecordID = wRecordID;
    cacheUpdateEvent2.cEventCount = cEventCount;
    cacheUpdateEvent2.generatedName_12 = generatedName_12;
    cacheUpdateEvent2.psEvents = psEvents;
    return cacheUpdateEvent2;
}

export interface UpdateImpact {
    cMsgID: number;
    wLength: number;
    dwRecordID: number;
    dwEventCount: number;
    dwReserve: number;
    wEvtRecSize: number;
    psImpactRecs: EVENT_REC_IMPACT[];
}

export interface FAIResp2 {
    pcACID: string; // Aircraft ID (7 chars)
    pcOrigin: string; // Origin airport (4 chars)
    pcDest: string; // Destination airport (4 chars)
    dwRecNo: number; // ASDI record number
    fStatus: number; // Track status code (3 bits)
    sArrDate: FE_DATETIME; // Date/Time of arrival
    sDepDate: FE_DATETIME; // Date/Time of departure
    sEntryDate: FE_DATETIME; // Date/Time of first aircraft entry
    sExitDate: FE_DATETIME; // Date/Time of last aircraft exit
}

export interface SMsgFAIResp2 {
    cMsgID: number; // Message ID
    wLength: number; // Message length
    iDepJDay: number; // Julian day of departure
    wRecSize: number; // Size of individual records
    cType: number; // 0:Pt in region, 1:text field
    cSeqNum: number; // Sequence number of message
    cIncomplete: number; // 0:Complete, 1:Incomplete
    cMFOnly: number; // 0:All flights, 1:Only MyFleet
    wCount: number; // Number of records to follow
    psRecords: FAIResp2[]; // Array of FAI_REC2 records
}

// @ts-ignore
let cacheSMsgFAIResp2: SMsgFAIResp2 = {};
// @ts-ignore
let cacheFAIResp2: FAIResp2 = {};

export function parseFAIResp2(buffer: ArrayBuffer): SMsgFAIResp2 {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const iDepJDay = view.getInt32(3, true);
    const wRecSize = view.getUint16(7, true);
    const cType = view.getUint8(9);
    const cSeqNum = view.getUint8(10);
    const cIncomplete = view.getUint8(11);
    const cMFOnly = view.getUint8(12);

    const pcReserved = new Uint8Array(buffer.slice(13, 23));

    const wCount = view.getUint16(23, true);

    const headerSize = 25; // Size of SMsgFAIResp2 header before records

    const psRecords: FAIResp2[] = [];
    let offset = headerSize;

    for (let i = 0; i < wCount; i++) {
        const chars_acid = [];
        for (let j = 0; j < 7; j++) {
            const char = view.getUint8(offset + j);
            if (char === 0) break;
            chars_acid.push(String.fromCharCode(char).trim());
        }
        const pcACID = chars_acid.join('');

        const chars_origin = [];
        for (let j = 0; j < 4; j++) {
            const char = view.getUint8(offset + 7 + j);
            if (char === 0) break;
            chars_origin.push(String.fromCharCode(char).trim());
        }
        const pcOrigin = chars_origin.join('');

        const chars_dest = [];
        for (let j = 0; j < 4; j++) {
            const char = view.getUint8(offset + 11 + j);
            if (char === 0) break;
            chars_dest.push(String.fromCharCode(char).trim());
        }
        const pcDest = chars_dest.join('');

        const dwRecNo = view.getUint32(offset + 15, true);

        // Read status (3 bits)
        const fStatus = view.getUint8(offset + 19) & 0x07;

        const sArrDate = parseFE_SMALLDATETIME(buffer.slice(offset + 20, offset + 22), iDepJDay);
        const sDepDate = parseFE_SMALLDATETIME(buffer.slice(offset + 22, offset + 24), iDepJDay);
        const sEntryDate = parseFE_SMALLDATETIME(buffer.slice(offset + 24, offset + 26), iDepJDay);
        const sExitDate = parseFE_SMALLDATETIME(buffer.slice(offset + 26, offset + 28), iDepJDay);

        psRecords.push({
            pcACID,
            pcOrigin,
            pcDest,
            dwRecNo,
            fStatus,
            sArrDate,
            sDepDate,
            sEntryDate,
            sExitDate
        });

        // Move to next record
        offset += wRecSize;
    }

    cacheSMsgFAIResp2 = {
        cMsgID,
        wLength,
        iDepJDay,
        wRecSize,
        cType,
        cSeqNum,
        cIncomplete,
        cMFOnly,
        wCount,
        psRecords
    };

    return cacheSMsgFAIResp2;
}

// @ts-ignore
let cacheUpdateImpact: UpdateImpact = {};

export function parseUpdateImpact(buffer: ArrayBuffer): UpdateImpact {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);

    // Decoding bits 18,8,6
    const tmp3 = view.getUint32(3, true);
    const dwRecordID = tmp3 & 0x3ffff;
    const dwEventCount = (tmp3 & 0x3fc0000) >>> 18;
    const dwReserve = (tmp3 & 0xfc000000) >>> 26;
    const wEvtRecSize = view.getUint16(7, true);
    const psImpactRecs = Array(dwEventCount);
    for (let i = 0; i < dwEventCount; i++) {
        psImpactRecs[i] = parseEVENT_REC_IMPACT(buffer.slice(41 + i * 96));
    }
    // Total size: -55 bytes
    cacheUpdateImpact.cMsgID = cMsgID;
    cacheUpdateImpact.wLength = wLength;
    cacheUpdateImpact.dwRecordID = dwRecordID;
    cacheUpdateImpact.dwEventCount = dwEventCount;
    cacheUpdateImpact.dwReserve = dwReserve;
    cacheUpdateImpact.wEvtRecSize = wEvtRecSize;
    cacheUpdateImpact.psImpactRecs = psImpactRecs;
    return cacheUpdateImpact;
}

export interface GeneralEvent {
    cMsgID: number;
    wLength: number;
    cSubjectType: number;
    pcSubject: string;
    pcDetail: string;
    wEventCode: number;
    cState: number;
    dwTime: number;
}

// @ts-ignore
let cacheGeneralEvent: GeneralEvent = {};

export function parseGeneralEvent(buffer: ArrayBuffer): GeneralEvent {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const cSubjectType = view.getUint8(3);
    const chars_4 = [];
    for (let i = 0; i < 10; i++) {
        const char = view.getUint8(4 + i);
        if (char === 0) break;
        chars_4.push(String.fromCharCode(char));
    }
    const pcSubject = chars_4.join('').trim();
    const chars_14 = [];
    for (let i = 0; i < 30; i++) {
        const char = view.getUint8(14 + i);
        if (char === 0) break;
        chars_14.push(String.fromCharCode(char));
    }
    const pcDetail = chars_14.join('').trim();
    const wEventCode = view.getUint16(44, true);
    const cState = view.getUint8(46);
    const dwTime = view.getUint32(47, true);
    // Total size: 51 bytes
    cacheGeneralEvent.cMsgID = cMsgID;
    cacheGeneralEvent.wLength = wLength;
    cacheGeneralEvent.cSubjectType = cSubjectType;
    cacheGeneralEvent.pcSubject = pcSubject;
    cacheGeneralEvent.pcDetail = pcDetail;
    cacheGeneralEvent.wEventCode = wEventCode;
    cacheGeneralEvent.cState = cState;
    cacheGeneralEvent.dwTime = dwTime;
    return cacheGeneralEvent;
}

export interface AptDivCounts {
    cMsgID: number;
    wLength: number;
    pcAPID: string;
    wDestCountPS: number;
    wDestCountActive: number;
    wAltCountPS: number;
    wAltCountActive: number;
    cDivEnRtAlnWide: number;
    cDivEnRtAlnNarr: number;
    cDivEnRtNetWide: number;
    cDivEnRtNetNarr: number;
    cDivEnRtOthWide: number;
    cDivEnRtOthNarr: number;
    cDivOnGdAlnWide: number;
    cDivOnGdAlnNarr: number;
    cDivOnGdNetWide: number;
    cDivOnGdNetNarr: number;
    cDivOnGdOthWide: number;
    cDivOnGdOthNarr: number;
    wNonDivOnGdNet: number;
}

// @ts-ignore
let cacheAptDivCounts: AptDivCounts = {};

export function parseAptDivCounts(buffer: ArrayBuffer): AptDivCounts {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const chars_3 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(3 + i);
        if (char === 0) break;
        chars_3.push(String.fromCharCode(char));
    }
    const pcAPID = chars_3.join('').trim();
    const wDestCountPS = view.getUint16(7, true);
    const wDestCountActive = view.getUint16(9, true);
    const wAltCountPS = view.getUint16(11, true);
    const wAltCountActive = view.getUint16(13, true);
    const cDivEnRtAlnWide = view.getUint8(15);
    const cDivEnRtAlnNarr = view.getUint8(16);
    const cDivEnRtNetWide = view.getUint8(17);
    const cDivEnRtNetNarr = view.getUint8(18);
    const cDivEnRtOthWide = view.getUint8(19);
    const cDivEnRtOthNarr = view.getUint8(20);
    const cDivOnGdAlnWide = view.getUint8(21);
    const cDivOnGdAlnNarr = view.getUint8(22);
    const cDivOnGdNetWide = view.getUint8(23);
    const cDivOnGdNetNarr = view.getUint8(24);
    const cDivOnGdOthWide = view.getUint8(25);
    const cDivOnGdOthNarr = view.getUint8(26);
    const wNonDivOnGdNet = view.getUint16(27, true);
    // Total size: 29 bytes
    cacheAptDivCounts.cMsgID = cMsgID;
    cacheAptDivCounts.wLength = wLength;
    cacheAptDivCounts.pcAPID = pcAPID;
    cacheAptDivCounts.wDestCountPS = wDestCountPS;
    cacheAptDivCounts.wDestCountActive = wDestCountActive;
    cacheAptDivCounts.wAltCountPS = wAltCountPS;
    cacheAptDivCounts.wAltCountActive = wAltCountActive;
    cacheAptDivCounts.cDivEnRtAlnWide = cDivEnRtAlnWide;
    cacheAptDivCounts.cDivEnRtAlnNarr = cDivEnRtAlnNarr;
    cacheAptDivCounts.cDivEnRtNetWide = cDivEnRtNetWide;
    cacheAptDivCounts.cDivEnRtNetNarr = cDivEnRtNetNarr;
    cacheAptDivCounts.cDivEnRtOthWide = cDivEnRtOthWide;
    cacheAptDivCounts.cDivEnRtOthNarr = cDivEnRtOthNarr;
    cacheAptDivCounts.cDivOnGdAlnWide = cDivOnGdAlnWide;
    cacheAptDivCounts.cDivOnGdAlnNarr = cDivOnGdAlnNarr;
    cacheAptDivCounts.cDivOnGdNetWide = cDivOnGdNetWide;
    cacheAptDivCounts.cDivOnGdNetNarr = cDivOnGdNetNarr;
    cacheAptDivCounts.cDivOnGdOthWide = cDivOnGdOthWide;
    cacheAptDivCounts.cDivOnGdOthNarr = cDivOnGdOthNarr;
    cacheAptDivCounts.wNonDivOnGdNet = wNonDivOnGdNet;
    return cacheAptDivCounts;
}

export interface AptDivFlight {
    cMsgID: number;
    wLength: number;
    pcAPID: string;
    cAPRecNum: number;
    pcACID: string;
    pcTail: string;
    pcOldDest: string;
    pcACType: string;
    pcOrigin: string;
    pcSectorDeskID: string;
    pcScratchPad: string;
    fcTimeDayOff: number;
    fcTimeHour: number;
    fcTimeMin: number;
    fbDivStatus: number;
    fbBodyType: number;
    fcBlkOutTimeHour: number;
    fcBlkOutTimeMin: number;
    fcDivRecPri: number;
    fcMsgType: number;
    fcBlkOutTimeDayOff: number;
    fcGroup: number;
    fcSubStatus: number;
    fcT1: number;
}

// @ts-ignore
let cacheAptDivFlight: AptDivFlight = {};

export function parseAptDivFlight(buffer: ArrayBuffer): AptDivFlight {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const chars_3 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(3 + i);
        if (char === 0) break;
        chars_3.push(String.fromCharCode(char));
    }
    const pcAPID = chars_3.join('').trim();
    const cAPRecNum = view.getUint8(7);
    const chars_8 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(8 + i);
        if (char === 0) break;
        chars_8.push(String.fromCharCode(char));
    }
    const pcACID = chars_8.join('').trim();
    const chars_15 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(15 + i);
        if (char === 0) break;
        chars_15.push(String.fromCharCode(char));
    }
    const pcTail = chars_15.join('').trim();
    const chars_22 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(22 + i);
        if (char === 0) break;
        chars_22.push(String.fromCharCode(char));
    }
    const pcOldDest = chars_22.join('').trim();
    const chars_26 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(26 + i);
        if (char === 0) break;
        chars_26.push(String.fromCharCode(char));
    }
    const pcACType = chars_26.join('').trim();
    const chars_30 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(30 + i);
        if (char === 0) break;
        chars_30.push(String.fromCharCode(char));
    }
    const pcOrigin = chars_30.join('').trim();
    const chars_34 = [];
    for (let i = 0; i < 5; i++) {
        const char = view.getUint8(34 + i);
        if (char === 0) break;
        chars_34.push(String.fromCharCode(char));
    }
    const pcSectorDeskID = chars_34.join('').trim();
    const chars_39 = [];
    for (let i = 0; i < 15; i++) {
        const char = view.getUint8(39 + i);
        if (char === 0) break;
        chars_39.push(String.fromCharCode(char));
    }
    const pcScratchPad = chars_39.join('').trim();

    // Decoding bits 3,5,6,1,1
    const tmp54 = view.getUint16(54, true);
    const fcTimeDayOff = tmp54 & 0x7;
    const fcTimeHour = (tmp54 & 0xf8) >>> 3;
    const fcTimeMin = (tmp54 & 0x3f00) >>> 8;
    const fbDivStatus = (tmp54 & 0x4000) >>> 14;
    const fbBodyType = (tmp54 & 0x8000) >>> 15;

    // Decoding bits 5,6,4,1
    const tmp56 = view.getUint16(56, true);
    const fcBlkOutTimeHour = tmp56 & 0x1f;
    const fcBlkOutTimeMin = (tmp56 & 0x7e0) >>> 5;
    const fcDivRecPri = (tmp56 & 0x7800) >>> 11;
    const fcMsgType = (tmp56 & 0x8000) >>> 15;

    // Decoding bits 3,2,2,1
    const tmp58 = view.getUint8(58);
    const fcBlkOutTimeDayOff = tmp58 & 0x7;
    const fcGroup = (tmp58 & 0x18) >>> 3;
    const fcSubStatus = (tmp58 & 0x60) >>> 5;
    const fcT1 = (tmp58 & 0x80) >>> 7;
    // Total size: 59 bytes
    cacheAptDivFlight.cMsgID = cMsgID;
    cacheAptDivFlight.wLength = wLength;
    cacheAptDivFlight.pcAPID = pcAPID;
    cacheAptDivFlight.cAPRecNum = cAPRecNum;
    cacheAptDivFlight.pcACID = pcACID;
    cacheAptDivFlight.pcTail = pcTail;
    cacheAptDivFlight.pcOldDest = pcOldDest;
    cacheAptDivFlight.pcACType = pcACType;
    cacheAptDivFlight.pcOrigin = pcOrigin;
    cacheAptDivFlight.pcSectorDeskID = pcSectorDeskID;
    cacheAptDivFlight.pcScratchPad = pcScratchPad;
    cacheAptDivFlight.fcTimeDayOff = fcTimeDayOff;
    cacheAptDivFlight.fcTimeHour = fcTimeHour;
    cacheAptDivFlight.fcTimeMin = fcTimeMin;
    cacheAptDivFlight.fbDivStatus = fbDivStatus;
    cacheAptDivFlight.fbBodyType = fbBodyType;
    cacheAptDivFlight.fcBlkOutTimeHour = fcBlkOutTimeHour;
    cacheAptDivFlight.fcBlkOutTimeMin = fcBlkOutTimeMin;
    cacheAptDivFlight.fcDivRecPri = fcDivRecPri;
    cacheAptDivFlight.fcMsgType = fcMsgType;
    cacheAptDivFlight.fcBlkOutTimeDayOff = fcBlkOutTimeDayOff;
    cacheAptDivFlight.fcGroup = fcGroup;
    cacheAptDivFlight.fcSubStatus = fcSubStatus;
    cacheAptDivFlight.fcT1 = fcT1;
    return cacheAptDivFlight;
}

export interface AptHoldFlight {
    cMsgID: number;
    wLength: number;
    pcAPID: string;
    cAPRecNum: number;
    pcACID: string;
    pcTail: string;
    pcDest: string;
    pcOrigin: string;
    pcACType: string;
    pcHoldFix: string;
    fcEnterHoldDay: number;
    fcEnterHoldHour: number;
    fcEnterHoldMin: number;
    fcT1: number;
    fcExitHoldDay: number;
    fcExitHoldHour: number;
    fcExitHoldMin: number;
    fcExitType: number;
    fcNextUpdateDay: number;
    fcNextUpdateHour: number;
    fcNextUpdateMin: number;
    fcT3: number;
    fcMsgType: number;
    fcFixType: number;
    fcHoldSource: number;
    fcT4: number;
    cCumulativeHoldMins: number;
}

// @ts-ignore
let cacheAptHoldFlight: AptHoldFlight = {};

export function parseAptHoldFlight(buffer: ArrayBuffer): AptHoldFlight {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const chars_3 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(3 + i);
        if (char === 0) break;
        chars_3.push(String.fromCharCode(char));
    }
    const pcAPID = chars_3.join('').trim();
    const cAPRecNum = view.getUint8(7);
    const chars_8 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(8 + i);
        if (char === 0) break;
        chars_8.push(String.fromCharCode(char));
    }
    const pcACID = chars_8.join('').trim();
    const chars_15 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(15 + i);
        if (char === 0) break;
        chars_15.push(String.fromCharCode(char));
    }
    const pcTail = chars_15.join('').trim();
    const chars_22 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(22 + i);
        if (char === 0) break;
        chars_22.push(String.fromCharCode(char));
    }
    const pcDest = chars_22.join('').trim();
    const chars_26 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(26 + i);
        if (char === 0) break;
        chars_26.push(String.fromCharCode(char));
    }
    const pcOrigin = chars_26.join('').trim();
    const chars_30 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(30 + i);
        if (char === 0) break;
        chars_30.push(String.fromCharCode(char));
    }
    const pcACType = chars_30.join('').trim();
    const chars_34 = [];
    for (let i = 0; i < 12; i++) {
        const char = view.getUint8(34 + i);
        if (char === 0) break;
        chars_34.push(String.fromCharCode(char));
    }
    const pcHoldFix = chars_34.join('').trim();

    // Decoding bits 3,5,6,2
    const tmp46 = view.getUint16(46, true);
    const fcEnterHoldDay = tmp46 & 0x7;
    const fcEnterHoldHour = (tmp46 & 0xf8) >>> 3;
    const fcEnterHoldMin = (tmp46 & 0x3f00) >>> 8;
    const fcT1 = (tmp46 & 0xc000) >>> 14;

    // Decoding bits 3,5,6,2
    const tmp48 = view.getUint16(48, true);
    const fcExitHoldDay = tmp48 & 0x7;
    const fcExitHoldHour = (tmp48 & 0xf8) >>> 3;
    const fcExitHoldMin = (tmp48 & 0x3f00) >>> 8;
    const fcExitType = (tmp48 & 0xc000) >>> 14;

    // Decoding bits 3,5,6,2
    const tmp50 = view.getUint16(50, true);
    const fcNextUpdateDay = tmp50 & 0x7;
    const fcNextUpdateHour = (tmp50 & 0xf8) >>> 3;
    const fcNextUpdateMin = (tmp50 & 0x3f00) >>> 8;
    const fcT3 = (tmp50 & 0xc000) >>> 14;

    // Decoding bits 1,3,3,1
    const tmp52 = view.getUint8(52);
    const fcMsgType = tmp52 & 0x1;
    const fcFixType = (tmp52 & 0xe) >>> 1;
    const fcHoldSource = (tmp52 & 0x70) >>> 4;
    const fcT4 = (tmp52 & 0x80) >>> 7;
    const cCumulativeHoldMins = view.getUint8(53);
    // Total size: 54 bytes
    cacheAptHoldFlight.cMsgID = cMsgID;
    cacheAptHoldFlight.wLength = wLength;
    cacheAptHoldFlight.pcAPID = pcAPID;
    cacheAptHoldFlight.cAPRecNum = cAPRecNum;
    cacheAptHoldFlight.pcACID = pcACID;
    cacheAptHoldFlight.pcTail = pcTail;
    cacheAptHoldFlight.pcDest = pcDest;
    cacheAptHoldFlight.pcOrigin = pcOrigin;
    cacheAptHoldFlight.pcACType = pcACType;
    cacheAptHoldFlight.pcHoldFix = pcHoldFix;
    cacheAptHoldFlight.fcEnterHoldDay = fcEnterHoldDay;
    cacheAptHoldFlight.fcEnterHoldHour = fcEnterHoldHour;
    cacheAptHoldFlight.fcEnterHoldMin = fcEnterHoldMin;
    cacheAptHoldFlight.fcT1 = fcT1;
    cacheAptHoldFlight.fcExitHoldDay = fcExitHoldDay;
    cacheAptHoldFlight.fcExitHoldHour = fcExitHoldHour;
    cacheAptHoldFlight.fcExitHoldMin = fcExitHoldMin;
    cacheAptHoldFlight.fcExitType = fcExitType;
    cacheAptHoldFlight.fcNextUpdateDay = fcNextUpdateDay;
    cacheAptHoldFlight.fcNextUpdateHour = fcNextUpdateHour;
    cacheAptHoldFlight.fcNextUpdateMin = fcNextUpdateMin;
    cacheAptHoldFlight.fcT3 = fcT3;
    cacheAptHoldFlight.fcMsgType = fcMsgType;
    cacheAptHoldFlight.fcFixType = fcFixType;
    cacheAptHoldFlight.fcHoldSource = fcHoldSource;
    cacheAptHoldFlight.fcT4 = fcT4;
    cacheAptHoldFlight.cCumulativeHoldMins = cCumulativeHoldMins;
    return cacheAptHoldFlight;
}

export interface AptMETAR {
    cMsgID: number;
    wLength: number;
    pcID: string;
    sReportDateTime: FE_DATETIME;
    wWindDir: number;
    cWindSpeed: number;
    cWindGusts: number;
    cVisLessGrt: number;
    wVisibility: number;
    cTemperature: number;
    cDewPoint: number;
    wAltimeter: number;
    wWeatherBitmap: number;
    wCeiling: number;
    cFlightCat: number;
}

// @ts-ignore
let cacheAptMETAR: AptMETAR = {};

export function parseAptMETAR(buffer: ArrayBuffer): AptMETAR {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const chars_3 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(3 + i);
        if (char === 0) break;
        chars_3.push(String.fromCharCode(char));
    }
    const pcID = chars_3.join('').trim();
    const sReportDateTime = parseFE_DATETIME(buffer.slice(7));
    const wWindDir = view.getUint16(13, true);
    const cWindSpeed = view.getUint8(15);
    const cWindGusts = view.getUint8(16);
    const cVisLessGrt = view.getUint8(17);
    const wVisibility = view.getUint16(18, true);
    const cTemperature = view.getInt8(20);
    const cDewPoint = view.getInt8(21);
    const wAltimeter = view.getUint16(22, true);
    const wWeatherBitmap = view.getUint16(24, true);
    const wCeiling = view.getUint16(26, true);
    const cFlightCat = view.getUint8(28);
    // Total size: 29 bytes
    cacheAptMETAR.cMsgID = cMsgID;
    cacheAptMETAR.wLength = wLength;
    cacheAptMETAR.pcID = pcID;
    cacheAptMETAR.sReportDateTime = sReportDateTime;
    cacheAptMETAR.wWindDir = wWindDir;
    cacheAptMETAR.cWindSpeed = cWindSpeed;
    cacheAptMETAR.cWindGusts = cWindGusts;
    cacheAptMETAR.cVisLessGrt = cVisLessGrt;
    cacheAptMETAR.wVisibility = wVisibility;
    cacheAptMETAR.cTemperature = cTemperature;
    cacheAptMETAR.cDewPoint = cDewPoint;
    cacheAptMETAR.wAltimeter = wAltimeter;
    cacheAptMETAR.wWeatherBitmap = wWeatherBitmap;
    cacheAptMETAR.wCeiling = wCeiling;
    cacheAptMETAR.cFlightCat = cFlightCat;
    return cacheAptMETAR;
}

export interface AptTAF {
    cMsgID: number;
    wLength: number;
    pcAPID: string;
    sReportDateTime: FE_DATETIME;
    cTAFCount: number;
    psTAF: SMSG_TAF_REC[];
}

// @ts-ignore
let cacheAptTAF: AptTAF = {};

export function parseAptTAF(buffer: ArrayBuffer): AptTAF {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const chars_3 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(3 + i);
        if (char === 0) break;
        chars_3.push(String.fromCharCode(char));
    }
    const pcAPID = chars_3.join('').trim();
    const sReportDateTime = parseFE_DATETIME(buffer.slice(7));
    const cTAFCount = view.getUint8(13);
    const psTAF = Array(cTAFCount);
    for (let i = 0; i < cTAFCount; i++) {
        psTAF[i] = parseSMSG_TAF_REC(buffer.slice(14 + i * 27));
    }
    // Total size: -13 bytes
    cacheAptTAF.cMsgID = cMsgID;
    cacheAptTAF.wLength = wLength;
    cacheAptTAF.pcAPID = pcAPID;
    cacheAptTAF.sReportDateTime = sReportDateTime;
    cacheAptTAF.cTAFCount = cTAFCount;
    cacheAptTAF.psTAF = psTAF;
    return cacheAptTAF;
}

export interface AptRVR {
    cMsgID: number;
    wLength: number;
    pcAPID: string;
    sReportDateTime: FE_DATETIME;
    cRVRCount: number;
    psRVR: SMSG_RVR_REC[];
}

// @ts-ignore
let cacheAptRVR: AptRVR = {};

export function parseAptRVR(buffer: ArrayBuffer): AptRVR {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const chars_3 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(3 + i);
        if (char === 0) break;
        chars_3.push(String.fromCharCode(char));
    }
    const pcAPID = chars_3.join('').trim();
    const sReportDateTime = parseFE_DATETIME(buffer.slice(7));
    const cRVRCount = view.getUint8(13);
    const psRVR = Array(cRVRCount);
    for (let i = 0; i < cRVRCount; i++) {
        psRVR[i] = parseSMSG_RVR_REC(buffer.slice(14 + i * 11));
    }
    // Total size: 3 bytes
    cacheAptRVR.cMsgID = cMsgID;
    cacheAptRVR.wLength = wLength;
    cacheAptRVR.pcAPID = pcAPID;
    cacheAptRVR.sReportDateTime = sReportDateTime;
    cacheAptRVR.cRVRCount = cRVRCount;
    cacheAptRVR.psRVR = psRVR;
    return cacheAptRVR;
}

export interface AptDelay {
    cMsgID: number;
    wLength: number;
    pcAPID: string;
    sReportDateTime: FE_DATETIME;
    cDelayCount: number;
    psDelay: SMSG_DELAY_REC[];
}

// @ts-ignore
let cacheAptDelay: AptDelay = {};

export function parseAptDelay(buffer: ArrayBuffer): AptDelay {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const chars_3 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(3 + i);
        if (char === 0) break;
        chars_3.push(String.fromCharCode(char));
    }
    const pcAPID = chars_3.join('').trim();
    const sReportDateTime = parseFE_DATETIME(buffer.slice(7));
    const cDelayCount = view.getUint8(13);
    const psDelay = Array(cDelayCount);
    for (let i = 0; i < cDelayCount; i++) {
        psDelay[i] = parseSMSG_DELAY_REC(buffer.slice(14 + i * 69));
    }
    // Total size: -55 bytes
    cacheAptDelay.cMsgID = cMsgID;
    cacheAptDelay.wLength = wLength;
    cacheAptDelay.pcAPID = pcAPID;
    cacheAptDelay.sReportDateTime = sReportDateTime;
    cacheAptDelay.cDelayCount = cDelayCount;
    cacheAptDelay.psDelay = psDelay;
    return cacheAptDelay;
}

export interface FPMMessageHeader {
    cMsgID: number;
    wLength: number;
    cSubCode: FPMMessageType;
}

export interface FPMGoToFlight {
    cSubCode: FPMMessageType.GoToFlight;
    pczACID: string;
    pczOrigin: string;
    pczTailNum: string;
    cEDTHour: number;
    cEDTMinute: number;
    dwEDTJulianDay: number;
}

export interface FPMUserResponse {
    cSubCode: FPMMessageType.FMPUserRs;
    dwRights: number;
    pczApplicationName: string;
    pczCompanyName: string;
    pczSupportPhone: string;
}

export interface FPMInfoMessage {
    cSubCode: FPMMessageType.TextInfo;
    cSizeOf: number;
    cMsgType: number; // INFO_SUCCESS, INFO_ERROR, INFO_WARN, INFO_INFO
    cMsgSrc: number; // one of the defined FE cSubCode types
    dwErrorID: number; // some numeric ID of error
    wTextLen: number; // length of text message provided
    pcText: string; // text message
}

export interface FPMStoreAreaMessage {
    cSubCode: FPMMessageType.StoreArea;
    cStatus: number; // 0=not active, 1=active
    cUserOnly: number; // 0=global, 1=user specific
    pczObjID: string; // object ID of draw object, null terminated
    pczLabelID: string; // drawn text ID of draw object, null terminated
    cStartHour: number; // begin hr (UTC)
    cStartMinute: number; // begin min
    dwStartJDay: number; // begin day, julian
    cEndHour: number; // end hr (UTC)
    cEndMinute: number; // end day, julian
    dwEndJDay: number; // end day, julian
    wLower: number | null; // lower altitude of area, 100s of feet, default to 0xFFFF if unknown
    wUpper: number | null; // upper altitude of area, 100s of feet, default to 0xFFFF if unknown
    wPolyPoints: number; // number of points in polygon object
    wOffset: number; // offset of where in the buffer the GEO_COORD array begins relative to first byte of the message
    cDaysOfWeekActive: number; // 0x01=Sun,0x02=Mon,0x04=Tues,0x08=Wed,0x10=Thu,0x20=Fri,0x40=Sat
    psPoints: GEO_COORD[]; // array of lat/lon points
}

export interface GEO_COORD {
    iLongitude: number; // longitude in 1/1000000 degrees
    iLatitude: number; // latitude in 1/1000000 degrees
}

export interface FPMDeleteAreaMessage {
    cSubCode: FPMMessageType.DeleteArea;
    pczObjID: string; // object ID of draw object, null terminated
}

// used for cFlag values of CSMsgIntRouteResponse structure
export enum FPMRouteFlags {
    clearList = 0x01, // clear all routes
    draw = 0x02, // draw this route on map
    lastRoute = 0x08 // last record indicator, used to trigger display of RouteAnalyzer
}

export interface FPMRouteResponse {
    cSubCode: FPMMessageType.RouteResponse | FPMMessageType.CalculateRouteRs;
    cFlags: number; // flag values
    cRouteType: string; // Type of routes
    pczRouteID: string; // route ID, null terminated
    pczOrigin: string; // Origin Airport, null terminated, or all nulls if not used
    pczDest: string; // Destination Airport, null terminated, or all nulls if not used
    pczACID: string | null;
    cDepHr: number; // EDT of flight, 24 if unknown
    cDepMin: number; // EDT of flight, 0 if unknown
    dwDepJDay: number; // EDT Julian date, 0 if unknown
    cRouteOffset: number; // number of bytes from start of record to where the route text string exists
    wRouteLen: number; // length of route text string
    wVarOrRelNumber: number | null; // variant or release number
    dwZeroFuelMass: number; // plane weight (lbs) at start of flight minus fuel weight, 0xFFFFFFFF if not provided
    dwMinBlockFuel: number; // minimum block fuel (lbs) for this flight, 0xFFFFFFFF if not provided (Note: block fuel = trip fuel + contigency)
    dwPlnBlockFuel: number; // planned block fuel (lbs) for this flight, 0xFFFFFFFF if not provided
    pcRoute: string; // route string
}

export interface FPMPlannedRouteResponse {
    cSubCode: FPMMessageType.PlannedRouteResponse;
    cSizeOf: number;
    pczRouteID: string; // route ID, null terminated
    wVarOrRelNumber: number; // variant or release number
    cRecordCount: number; // Number of planned fixed records
    psFixRecs: WaypointPlanRec[]; // array of PlnFixRecord struct records
}

export interface WaypointPlanRec {
    cStructSize: number;
    pcID: string; // Fix ID (not null terminated, trailing blanks)
    wCruiseAlt: number | null; // cruising altitude, 100s of feet (0-999)
    dwFuelOnBoard: number | null; // Fuel On Board (lbs) at this waypoint
    dwFEWPTimeOver: number; // Time over Fix, FEWP time
}

export interface FPMVerifyRouteResponse {
    cSubCode: FPMMessageType.VerifyRouteResponse;
    cRouteOffset: number; // number of bytes from start of record to where the route text string exists
    wRouteLen: number; // length of route string
    pczRouteID: string; // route ID, null terminated
    pcRoute: string; // route string
}

// Header-only notifications (no payload)
export interface FPMAckMessage {
    cSubCode: FPMMessageType.GoToFlightRs | FPMMessageType.CalculateRouteNotif;
}

export type FPMMessage =
    | FPMAckMessage
    | FPMGoToFlight
    | FPMUserResponse
    | FPMInfoMessage
    | FPMStoreAreaMessage
    | FPMDeleteAreaMessage
    | FPMRouteResponse
    | FPMPlannedRouteResponse
    | FPMVerifyRouteResponse;

export function parseFPMIntegration(buffer: ArrayBuffer): FPMMessage | undefined {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const cSubCode = view.getUint8(3);

    logger.info(`FPM message received: cSubCode=${cSubCode}`);
    switch (cSubCode) {
        case FPMMessageType.GoToFlight:
            return parseFPMGoToFlight(view);
        case FPMMessageType.GoToFlightRs:
            // no message to parse, just a notification
            return { cSubCode: FPMMessageType.GoToFlightRs };
        case FPMMessageType.FMPUserRs:
            return parseFPMUserResponse(view);
        case FPMMessageType.StoreArea:
            return parseFPMStoreAreaMessage(view);
        case FPMMessageType.DeleteArea:
            return parseFPMDeleteAreaMessage(view);
        case FPMMessageType.RouteResponse:
        case FPMMessageType.CalculateRouteRs:
            return parseFPMRouteResponse(view, cSubCode as FPMMessageType.RouteResponse | FPMMessageType.CalculateRouteRs);
        case FPMMessageType.CalculateRouteNotif:
            logger.info('FPM Calculate Route Notification received');
            // no message to parse, just a notification
            return { cSubCode: FPMMessageType.CalculateRouteNotif };
        case FPMMessageType.PlannedRouteResponse:
            return parseFPMPlannedRouteResponse(view);
        case FPMMessageType.VerifyRouteResponse:
            return parseFPMVerifyRouteResponse(view);
        case FPMMessageType.TextInfo:
            return parseFPMInfoMessage(view);
        default:
            console.warn(`Unknown FPM message subcode: ${cSubCode}`);
            return undefined;
    }
}

function parseFPMGoToFlight(view: DataView): FPMGoToFlight {
    const chars_4 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(4 + i);
        if (char === 0) break;
        chars_4.push(String.fromCharCode(char));
    }
    const pczACID = chars_4.join('').trim();

    const chars_11 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(11 + i);
        if (char === 0) break;
        chars_11.push(String.fromCharCode(char));
    }
    const pczOrigin = chars_11.join('').trim();

    const chars_15 = [];
    for (let i = 0; i < 7; i++) {
        const char = view.getUint8(15 + i);
        if (char === 0) break;
        chars_15.push(String.fromCharCode(char));
    }
    const pczTailNum = chars_15.join('').trim();

    const cEDTHour = view.getUint8(22);
    const cEDTMinute = view.getUint8(23);
    const dwEDTJulianDay = view.getUint32(24, true);

    return {
        cSubCode: FPMMessageType.GoToFlight,
        pczACID: pczACID,
        pczOrigin: pczOrigin,
        pczTailNum: pczTailNum,
        cEDTHour: cEDTHour,
        cEDTMinute: cEDTMinute,
        dwEDTJulianDay: dwEDTJulianDay
    } as FPMGoToFlight;
}
function parseFPMUserResponse(view: DataView): FPMUserResponse {
    const dwRights = view.getUint32(4, true);
    const chars_app = [];
    for (let i = 0; i < 31; i++) {
        const char = view.getUint8(8 + i);
        if (char === 0) break;
        chars_app.push(String.fromCharCode(char));
    }
    const pczApplicationName = chars_app.join('').trim();
    const chars_company = [];
    for (let i = 0; i < 31; i++) {
        const char = view.getUint8(39 + i);
        if (char === 0) break;
        chars_company.push(String.fromCharCode(char));
    }
    const pczCompanyName = chars_company.join('').trim();
    const chars_phone = [];
    for (let i = 0; i < 17; i++) {
        const char = view.getUint8(70 + i);
        if (char === 0) break;
        chars_phone.push(String.fromCharCode(char));
    }
    const pczSupportPhone = chars_phone.join('').trim();

    return {
        cSubCode: FPMMessageType.FMPUserRs,
        dwRights: dwRights,
        pczApplicationName: pczApplicationName,
        pczCompanyName: pczCompanyName,
        pczSupportPhone: pczSupportPhone
    } as FPMUserResponse;
}

function parseFPMInfoMessage(view: DataView): FPMInfoMessage {
    const cSizeOf = view.getUint8(4);
    const cMsgType = view.getUint8(5);
    const cMsgSrc = view.getUint8(6);
    const dwErrorID = view.getUint32(7, true);
    const wTextLen = view.getUint16(11, true);

    let offset = 13;
    const chars_text = [];
    for (let i = 0; i < wTextLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_text.push(String.fromCharCode(char));
    }
    const pcText = chars_text.join('').trim();

    return {
        cSubCode: FPMMessageType.TextInfo,
        cSizeOf: cSizeOf,
        cMsgType: cMsgType,
        cMsgSrc: cMsgSrc,
        dwErrorID: dwErrorID,
        wTextLen: wTextLen,
        pcText: pcText
    } as FPMInfoMessage;
}

function parseFPMStoreAreaMessage(view: DataView): FPMStoreAreaMessage {
    // Parse combined status byte (same as request)
    const statusByte = view.getUint8(4);
    const cStatus = statusByte & 0x01;
    const cUserOnly = (statusByte & 0x02) >> 1;

    const chars_5 = [];
    for (let i = 0; i < 31; i++) {
        const char = view.getUint8(5 + i);
        if (char === 0) break;
        chars_5.push(String.fromCharCode(char));
    }
    const pczObjID = chars_5.join('').trim();

    const chars_36 = [];
    for (let i = 0; i < 31; i++) {
        const char = view.getUint8(36 + i);
        if (char === 0) break;
        chars_36.push(String.fromCharCode(char));
    }
    const pczLabelID = chars_36.join('').trim();

    const cStartHour = view.getUint8(67);
    const cStartMinute = view.getUint8(68);
    const dwStartJDay = view.getUint32(69, true);

    const cEndHour = view.getUint8(73);
    const cEndMinute = view.getUint8(74);
    const dwEndJDay = view.getUint32(75, true);

    const wLower = view.getUint16(79, true);
    const wUpper = view.getUint16(81, true);

    const wPolyPoints = view.getUint16(83, true);
    const wOffset = view.getUint16(85, true);

    const cDaysOfWeekActive = view.getUint8(87);

    // Parse GEO_COORD array
    const psPoints: GEO_COORD[] = [];
    for (let i = 0; i < wPolyPoints; i++) {
        const coordOffset = wOffset + i * 8;
        psPoints.push({
            iLongitude: view.getInt32(coordOffset, true),
            iLatitude: view.getInt32(coordOffset + 4, true)
        });
    }

    return {
        cSubCode: FPMMessageType.StoreArea,
        cStatus: cStatus,
        cUserOnly: cUserOnly,
        pczObjID: pczObjID,
        pczLabelID: pczLabelID,
        cStartHour: cStartHour,
        cStartMinute: cStartMinute,
        dwStartJDay: dwStartJDay,
        cEndHour: cEndHour,
        cEndMinute: cEndMinute,
        dwEndJDay: dwEndJDay,
        wLower: wLower === 0xffff ? null : wLower,
        wUpper: wUpper === 0xffff ? null : wUpper,
        wPolyPoints: wPolyPoints,
        wOffset: wOffset,
        cDaysOfWeekActive: cDaysOfWeekActive,
        psPoints: psPoints
    } as FPMStoreAreaMessage;
}

function parseFPMDeleteAreaMessage(view: DataView): FPMDeleteAreaMessage {
    const chars_4 = [];
    for (let i = 0; i < 31; i++) {
        const char = view.getUint8(4 + i);
        if (char === 0) break;
        chars_4.push(String.fromCharCode(char));
    }
    const pczObjID = chars_4.join('').trim();

    return {
        cSubCode: FPMMessageType.DeleteArea,
        pczObjID: pczObjID
    } as FPMDeleteAreaMessage;
}

function parseFPMRouteResponse(view: DataView, cSubCode: FPMMessageType.RouteResponse | FPMMessageType.CalculateRouteRs): FPMRouteResponse {
    const cFlags = view.getUint8(4);
    const cRouteType = String.fromCharCode(view.getUint8(5));

    const chars_6 = [];
    for (let i = 0; i < 16; i++) {
        const char = view.getUint8(6 + i);
        if (char === 0) break;
        chars_6.push(String.fromCharCode(char));
    }
    const pczRouteID = chars_6.join('').trim();

    const chars_22 = [];
    for (let i = 0; i < 5; i++) {
        const char = view.getUint8(22 + i);
        if (char === 0) break;
        chars_22.push(String.fromCharCode(char));
    }
    const pczOrigin = chars_22.join('').trim();

    const chars_27 = [];
    for (let i = 0; i < 5; i++) {
        const char = view.getUint8(27 + i);
        if (char === 0) break;
        chars_27.push(String.fromCharCode(char));
    }
    const pczDest = chars_27.join('').trim();

    const chars_32 = [];
    for (let i = 0; i < 8; i++) {
        const char = view.getUint8(32 + i);
        if (char === 0) break;
        chars_32.push(String.fromCharCode(char));
    }
    const pczACID = chars_32.join('').trim();

    const cDepHr = view.getUint8(40);
    const cDepMin = view.getUint8(41);
    const dwDepJDay = view.getUint32(42, true);

    const cRouteOffset = view.getUint8(46);
    const wRouteLen = view.getUint16(47, true);
    const wVarOrRelNumber = view.getUint16(49, true);
    const dwZeroFuelMass = view.getUint32(51, true);
    const dwMinBlockFuel = view.getUint32(55, true);
    const dwPlnBlockFuel = view.getUint32(59, true);

    // Route string starts at offset 63
    const chars_route = [];
    for (let i = 0; i < wRouteLen; i++) {
        const char = view.getUint8(63 + i);
        chars_route.push(String.fromCharCode(char));
    }
    const pcRoute = chars_route.join('');

    return {
        cSubCode: cSubCode,
        cFlags: cFlags,
        cRouteType: cRouteType,
        pczRouteID: pczRouteID,
        pczOrigin: pczOrigin,
        pczDest: pczDest,
        pczACID: pczACID[0] === '\0' ? null : pczACID,
        cDepHr: cDepHr,
        cDepMin: cDepMin,
        dwDepJDay: dwDepJDay,
        cRouteOffset: cRouteOffset,
        wRouteLen: wRouteLen,
        wVarOrRelNumber: wVarOrRelNumber === 0xffff ? null : wVarOrRelNumber,
        dwZeroFuelMass: dwZeroFuelMass,
        dwMinBlockFuel: dwMinBlockFuel,
        dwPlnBlockFuel: dwPlnBlockFuel,
        pcRoute: pcRoute
    } as FPMRouteResponse;
}

function parseFPMPlannedRouteResponse(view: DataView): FPMPlannedRouteResponse {
    const cSizeOf = view.getUint8(4);

    const chars_5 = [];
    for (let i = 0; i < 16; i++) {
        const char = view.getUint8(5 + i);
        if (char === 0) break;
        chars_5.push(String.fromCharCode(char));
    }
    const pczRouteID = chars_5.join('').trim();

    const wVarOrRelNumber = view.getUint16(21, true);
    const cRecordCount = view.getUint8(23);

    const psFixRecs: WaypointPlanRec[] = [];
    let offset = 24;
    let byteLength = view.byteLength;
    for (let i = 0; i < cRecordCount && offset + 26 <= byteLength; i++) {
        const pcID = [];
        for (let j = 0; j < 15; j++) {
            const char = view.getUint8(offset + 1 + j);
            if (char === 0) break;
            pcID.push(String.fromCharCode(char));
        }
        const wCruiseAlt = view.getUint16(offset + 16, true);
        const dwFuelOnBoard = view.getUint32(offset + 18, true);
        psFixRecs.push({
            cStructSize: view.getUint8(offset),
            pcID: pcID.join('').trim(),
            wCruiseAlt: wCruiseAlt === 0xffff ? null : wCruiseAlt,
            dwFuelOnBoard: dwFuelOnBoard === 0xffffffff ? null : dwFuelOnBoard,
            dwFEWPTimeOver: view.getUint32(offset + 22, true)
        });
        offset += 26;
    }

    return {
        cSubCode: FPMMessageType.PlannedRouteResponse,
        cSizeOf: cSizeOf,
        pczRouteID: pczRouteID,
        wVarOrRelNumber: wVarOrRelNumber,
        cRecordCount: cRecordCount,
        psFixRecs: psFixRecs
    } as FPMPlannedRouteResponse;
}

function parseFPMVerifyRouteResponse(view: DataView): FPMVerifyRouteResponse {
    const cRouteOffset = view.getUint8(4);
    const wRouteLen = view.getUint16(5, true);

    const chars_8 = [];
    for (let i = 0; i < 16; i++) {
        const char = view.getUint8(7 + i);
        if (char === 0) break;
        chars_8.push(String.fromCharCode(char));
    }
    const pczRouteID = chars_8.join('').trim();
    let offset = 23;
    const chars_route: string[] = [];
    for (let i = 0; i < wRouteLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_route.push(String.fromCharCode(char));
    }
    const pcRoute = chars_route.join('').trim();

    return {
        cSubCode: FPMMessageType.VerifyRouteResponse,
        cRouteOffset: cRouteOffset,
        wRouteLen: wRouteLen,
        pczRouteID: pczRouteID,
        pcRoute: pcRoute
    } as FPMVerifyRouteResponse;
}

export interface APMonConfigUpdate {
    cMsgID: number;
    wLength: number;
    cRecFixedLen: number;
    pcAPID: string;
    pcTFMWeather: string;
    wArrRate: number;
    wDepRate: number;
    cDeiceStatus: number;
    sTFMEventTime: FE_DATETIME;
    sTFMEntryTime: FE_DATETIME;
    sDeiceEventTime: FE_DATETIME;
    sDeiceEntryTime: FE_DATETIME;
    cArrRunwayIDLen: number;
    cDepRunwayIDLen: number;
    cArrRunwayProcLen: number;
    cDepRunwayProcLen: number;
    cStartAARLen: number;
    cAARAdjustLen: number;
    dwArrRemarksLen: number;
    dwDepRemarksLen: number;
    dwDeiceRemarksLen: number;
    dwSpare: number;
    arrRunway: string;
    depRunway: string;
    arrRunwayProc: string;
    depRunwayProc: string;
    startArr: string;
    arrAdjust: string;
    arrRemarks: string;
    depRemarks: string;
    deiceRemarks: string;
}

// @ts-ignore
let cacheAPMonConfigUpdate: APMonConfigUpdate = {};

export function parseAPMonConfigUpdate(buffer: ArrayBuffer): APMonConfigUpdate {
    const view = new DataView(buffer);
    const cMsgID = view.getUint8(0);
    const wLength = view.getUint16(1, true);
    const cRecFixedLen = view.getUint8(3);
    const chars_4 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(4 + i);
        if (char === 0) break;
        chars_4.push(String.fromCharCode(char));
    }
    const pcAPID = chars_4.join('').trim();
    const chars_8 = [];
    for (let i = 0; i < 4; i++) {
        const char = view.getUint8(8 + i);
        if (char === 0) break;
        chars_8.push(String.fromCharCode(char));
    }
    const pcTFMWeather = chars_8.join('').trim();
    const wArrRate = view.getUint16(12, true);
    const wDepRate = view.getUint16(14, true);
    const cDeiceStatus = view.getUint8(16);
    const sTFMEventTime = parseFE_DATETIME(buffer.slice(17));
    const sTFMEntryTime = parseFE_DATETIME(buffer.slice(23));
    const sDeiceEventTime = parseFE_DATETIME(buffer.slice(29));
    const sDeiceEntryTime = parseFE_DATETIME(buffer.slice(35));
    const cArrRunwayIDLen = view.getUint8(41);
    const cDepRunwayIDLen = view.getUint8(42);
    const cArrRunwayProcLen = view.getUint8(43);
    const cDepRunwayProcLen = view.getUint8(44);
    const cStartAARLen = view.getUint8(45);
    const cAARAdjustLen = view.getUint8(46);

    // Decoding bits 10,10,10,2
    const tmp47 = view.getUint32(47, true);
    const dwArrRemarksLen = tmp47 & 0x3ff;
    const dwDepRemarksLen = (tmp47 & 0xffc00) >>> 10;
    const dwDeiceRemarksLen = (tmp47 & 0x3ff00000) >>> 20;
    const dwSpare = (tmp47 & 0xc0000000) >>> 30;
    let offset = 51;
    const chars_51 = [];
    for (let i = 0; i < cArrRunwayIDLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_51.push(String.fromCharCode(char));
    }
    offset += cArrRunwayIDLen;
    const arrRunway = chars_51.join('').trim();
    const chars_50 = [];
    for (let i = 0; i < cDepRunwayIDLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_50.push(String.fromCharCode(char));
    }
    offset += cDepRunwayIDLen;
    const depRunway = chars_50.join('').trim();
    const chars_49 = [];
    for (let i = 0; i < cArrRunwayProcLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_49.push(String.fromCharCode(char));
    }
    offset += cArrRunwayProcLen;
    const arrRunwayProc = chars_49.join('').trim();
    const chars_48 = [];
    for (let i = 0; i < cDepRunwayProcLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_48.push(String.fromCharCode(char));
    }
    offset += cDepRunwayProcLen;
    const depRunwayProc = chars_48.join('').trim();
    const chars_47 = [];
    for (let i = 0; i < cStartAARLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_47.push(String.fromCharCode(char));
    }
    offset += cStartAARLen;
    const startArr = chars_47.join('').trim();
    const chars_46 = [];
    for (let i = 0; i < cAARAdjustLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_46.push(String.fromCharCode(char));
    }
    offset += cAARAdjustLen;
    const arrAdjust = chars_46.join('').trim();
    const chars_45 = [];
    for (let i = 0; i < dwArrRemarksLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_45.push(String.fromCharCode(char));
    }
    offset += dwArrRemarksLen;
    const arrRemarks = chars_45.join('').trim();
    const chars_44 = [];
    for (let i = 0; i < dwDepRemarksLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_44.push(String.fromCharCode(char));
    }
    offset += dwDepRemarksLen;
    const depRemarks = chars_44.join('').trim();
    const chars_43 = [];
    for (let i = 0; i < dwDeiceRemarksLen; i++) {
        const char = view.getUint8(offset + i);
        if (char === 0) break;
        chars_43.push(String.fromCharCode(char));
    }
    offset += dwDeiceRemarksLen;
    const deiceRemarks = chars_43.join('').trim();
    // Total size: 42 bytes
    cacheAPMonConfigUpdate.cMsgID = cMsgID;
    cacheAPMonConfigUpdate.wLength = wLength;
    cacheAPMonConfigUpdate.cRecFixedLen = cRecFixedLen;
    cacheAPMonConfigUpdate.pcAPID = pcAPID;
    cacheAPMonConfigUpdate.pcTFMWeather = pcTFMWeather;
    cacheAPMonConfigUpdate.wArrRate = wArrRate;
    cacheAPMonConfigUpdate.wDepRate = wDepRate;
    cacheAPMonConfigUpdate.cDeiceStatus = cDeiceStatus;
    cacheAPMonConfigUpdate.sTFMEventTime = sTFMEventTime;
    cacheAPMonConfigUpdate.sTFMEntryTime = sTFMEntryTime;
    cacheAPMonConfigUpdate.sDeiceEventTime = sDeiceEventTime;
    cacheAPMonConfigUpdate.sDeiceEntryTime = sDeiceEntryTime;
    cacheAPMonConfigUpdate.cArrRunwayIDLen = cArrRunwayIDLen;
    cacheAPMonConfigUpdate.cDepRunwayIDLen = cDepRunwayIDLen;
    cacheAPMonConfigUpdate.cArrRunwayProcLen = cArrRunwayProcLen;
    cacheAPMonConfigUpdate.cDepRunwayProcLen = cDepRunwayProcLen;
    cacheAPMonConfigUpdate.cStartAARLen = cStartAARLen;
    cacheAPMonConfigUpdate.cAARAdjustLen = cAARAdjustLen;
    cacheAPMonConfigUpdate.dwArrRemarksLen = dwArrRemarksLen;
    cacheAPMonConfigUpdate.dwDepRemarksLen = dwDepRemarksLen;
    cacheAPMonConfigUpdate.dwDeiceRemarksLen = dwDeiceRemarksLen;
    cacheAPMonConfigUpdate.dwSpare = dwSpare;
    cacheAPMonConfigUpdate.arrRunway = arrRunway;
    cacheAPMonConfigUpdate.depRunway = depRunway;
    cacheAPMonConfigUpdate.arrRunwayProc = arrRunwayProc;
    cacheAPMonConfigUpdate.depRunwayProc = depRunwayProc;
    cacheAPMonConfigUpdate.startArr = startArr;
    cacheAPMonConfigUpdate.arrAdjust = arrAdjust;
    cacheAPMonConfigUpdate.arrRemarks = arrRemarks;
    cacheAPMonConfigUpdate.depRemarks = depRemarks;
    cacheAPMonConfigUpdate.deiceRemarks = deiceRemarks;
    return cacheAPMonConfigUpdate;
}

function emptyParse(_: ArrayBuffer) {
    return null;
}

export type WeatherProductHistoryRecord = {
    items: WeatherProductHistoryItem[];
};

export type WeatherProductHistoryItem = {
    id: number;
    index: number;
    time1: number | null;
    time2: number | null;
};

function parseWeatherProdHistory(buffer: ArrayBuffer): WeatherProductHistoryRecord {
    const result: WeatherProductHistoryRecord = {
        items: []
    };

    const view = new DataView(buffer);
    const count = view.getUint16(3, true);

    for (let i = 0; i < count; i++) {
        const offset = 5 + i * 11;
        result.items.push({
            id: view.getUint16(offset, true),
            index: view.getUint8(offset + 2),
            time1: valueOrNull(view.getUint32(offset + 3, true), 0xffffffff),
            time2: valueOrNull(view.getUint32(offset + 7, true), 0xffffffff)
        });
    }

    return result;
}

const parseFunctions: Partial<{ [key in MessageId]: (buffer: ArrayBuffer) => any | null }> = {
    [MessageId.Unknown]: emptyParse,
    [MessageId.Init1]: parseInit1,
    [MessageId.Init1b]: parseInit1b,
    [MessageId.UpdatePropNA2]: parseUpdatePropNA2,
    [MessageId.UpdatePropA2]: parseUpdatePropA2,
    [MessageId.NewPlane]: parseNewPlane,
    [MessageId.NewPlaneEx]: parseNewPlaneEx,
    [MessageId.Init2d]: parseInit2d,
    [MessageId.Init1Start]: parseInit1Start,
    [MessageId.Init1End]: parseInit1End,
    [MessageId.Init2Start]: parseInit2Start,
    [MessageId.Init2End]: parseInit2End,
    [MessageId.UpdateStart]: parseUpdateStart,
    [MessageId.UpdateEnd]: parseUpdateEnd,
    [MessageId.LoginResponse]: parseLoginResp,
    [MessageId.UpdateTZsfc1]: parseUpdateTZsfc1,
    [MessageId.UpdateTZsfc2]: parseUpdateTZsfc2,
    [MessageId.UpdateSfcLow]: parseUpdateSfcLow,
    [MessageId.UpdateTZ3]: parseUpdateTZ3,
    [MessageId.UpdateDZb]: parseUpdateDZb,
    [MessageId.UpdateADSBMed]: parseUpdateADSBMed,
    [MessageId.UpdateADSBLow]: parseUpdateADSBLow,
    [MessageId.UpdateStatus]: parseUpdateStatus,
    [MessageId.UpdateStatus2]: parseUpdateStatus2,
    [MessageId.UpdateACID]: parseUpdateACID,
    [MessageId.WthrProdInfoV2]: parseWthrProdInfoV2,
    [MessageId.WthrProdStatusV2]: parseWthrProdStatusV2,
    [MessageId.TimeAdjust]: parseTimeAdjust,
    [MessageId.StartupComplete]: parseStartupComplete,
    [MessageId.GeneralEvent]: parseGeneralEvent,
    [MessageId.Update3PDIHi]: parseUpdate3PDIHi,
    [MessageId.Update3PDIMed]: parseUpdate3PDIMed,
    [MessageId.Update3PDILo]: parseUpdate3PDILo,
    [MessageId.AirportDiversionSummary]: parseAptDivCounts,
    [MessageId.AirportDivertedFlight]: parseAptDivFlight,
    [MessageId.AirportFlightHolding]: parseAptHoldFlight,
    [MessageId.AirportMetar]: parseAptMETAR,
    [MessageId.AirportTaf]: parseAptTAF,
    [MessageId.AirportRvr]: parseAptRVR,
    [MessageId.AirportDelay]: parseAptDelay,
    [MessageId.AirportConfig]: parseAPMonConfigUpdate,
    [MessageId.FAIResp2]: parseFAIResp2,
    [MessageId.UpdateImpact]: parseUpdateImpact,
    [MessageId.UpdateEvent]: parseUpdateEvent,
    [MessageId.UpdateEvent2]: parseUpdateEvent2,
    [MessageId.MyFleetLastKnownLocationResponse]: parseMFLKLResp,
    [MessageId.FPMIntegration]: parseFPMIntegration,
    [MessageId.WeatherProdHistory]: parseWeatherProdHistory
} as const;

function parse(message: MessageId, buffer: ArrayBuffer): any | null {
    return parseFunctions[message]?.call(undefined, buffer) ?? null;
}

function valueOrNull(value: number, nullValue: number): number | null {
    return value === nullValue ? null : value;
}

export default {
    parse
};
